import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Package, Plus, UsersThree, X, WhatsappLogo, EnvelopeSimple, Trash, Gear, Tray, MagnifyingGlass } from '@phosphor-icons/react';
import UserPicker from '../../components/UserPicker';
import ListSearch, { filterBySearch, PRODUCT_SEARCH_KEYS, TEAM_MEMBER_SEARCH_KEYS } from '../../components/ListSearch';
import SearchableSelect from '../../components/SearchableSelect';
import { useAuth } from '../../App';
import { ModuleShell } from '../../components/module-ui';

import { BACKEND_URL, apiPath } from '../../config/apiBase';
import ProductLocationsPanel from './ProductLocationsPanel';
import ClientWidgetsPanel from './ClientWidgetsPanel';
import CompaniesPanel from './CompaniesPanel';
import ClientsBranchesPanel from './ClientsBranchesPanel';
import { notifySuccess, notifyError } from '../../lib/notify';
const API = apiPath('/api/products');
const WA = apiPath('/api/whatsapp-tasks');

// Product → Access: Product Management modules a manager / admin can grant a
// team member on one product. Managers and admins always have all of them;
// members have none until granted. Keys match backend TEAM_FEATURES.
const TEAM_ACCESS_OPTIONS = [
  { id: 'products', label: 'Product settings', hint: 'General, ticket routing, new ticket assignment' },
  { id: 'data_sources', label: 'Data sources', hint: 'Add and edit this product\'s datasets' },
  { id: 'clients', label: 'Clients and branches', hint: 'Add clients and their branches' },
  { id: 'whatsapp', label: 'WhatsApp mapping', hint: 'This product\'s WhatsApp groups' },
  { id: 'email_mapping', label: 'Email mapping', hint: 'This product\'s mailboxes' },
  { id: 'widgets', label: 'Client widgets', hint: 'This product\'s website widgets' },
  { id: 'team', label: 'Team', hint: 'Members, departments, ticket raisers' },
  { id: 'access', label: 'Access', hint: 'Grant modules and ticket actions to others' },
  { id: 'team_rules', label: 'Team rules', hint: 'Multi-product roles for this team' },
];
// Tabs inside a selected product, each unlocked by one Access module.
const PRODUCT_TABS = [
  { id: 'general', label: 'General', module: 'products' },
  { id: 'routing', label: 'Ticket Routing', module: 'products' },
  { id: 'assignment', label: 'New ticket assignment', module: 'products' },
  { id: 'locations', label: 'Data sources', module: 'data_sources' },
  { id: 'clients_branches', label: 'Clients & branches', module: 'clients' },
  { id: 'whatsapp', label: 'WhatsApp mapping', module: 'whatsapp' },
  { id: 'email', label: 'Email mapping', module: 'email_mapping' },
  { id: 'widgets', label: 'Client widgets', module: 'widgets' },
  { id: 'team', label: 'Team', module: 'team' },
  { id: 'access', label: 'Access', module: 'access' },
  { id: 'team_rules', label: 'Team rules', module: 'team_rules' },
];
const EMAIL_MAPPING = apiPath('/api/email-mapping');

function authHeaders() {
  const token = localStorage.getItem('token');
  return { withCredentials: true, headers: { Authorization: `Bearer ${token}` } };
}

/** Map product clients → SearchableSelect options (label includes parent group/type). */
function clientSelectOptions(clients, { currentGroupId, legacyValue } = {}) {
  const opts = (clients || []).map((c) => {
    const parent = c.parent_value ? `[${c.parent_value}] ` : '';
    const takenByOther = Boolean(
      currentGroupId && c.already_mapped && c.mapped_group_id !== currentGroupId,
    );
    const label = `${parent}${c.label || c.value}${takenByOther ? ' (mapped)' : ''}`;
    return {
      value: c.value,
      label,
      searchText: `${c.parent_value || ''} ${c.label || ''} ${c.value || ''}`,
      disabled: takenByOther,
    };
  });
  if (legacyValue && !opts.some((o) => o.value === legacyValue)) {
    opts.unshift({
      value: legacyValue,
      label: `${legacyValue} (legacy — reselect)`,
      searchText: String(legacyValue),
    });
  }
  return opts;
}

function slugFromName(name) {
  return name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
}

const DOMAIN_OPTIONS = [
  { key: 'product', label: 'App Support' },
  { key: 'hardware', label: 'Hardware' },
  { key: 'software', label: 'Software' },
];

const EMPTY_EMAIL_FORM = {
  email_address: '',
  label: '',
  imap_host: '',
  imap_port: '993',
  imap_username: '',
  imap_password: '',
  imap_folder: 'INBOX',
  imap_use_ssl: true,
};

function DomainCheckboxes({ value, onChange }) {
  const toggle = (k) => {
    const next = value.includes(k) ? value.filter((d) => d !== k) : [...value, k];
    onChange(next);
  };
  return (
    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
      {DOMAIN_OPTIONS.map((d) => (
        <label key={d.key} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
          <input type="checkbox" checked={value.includes(d.key)} onChange={() => toggle(d.key)} />
          {d.label}
        </label>
      ))}
    </div>
  );
}

function ProductsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPlatformAdmin = ['super_admin', 'admin'].includes(user?.role);

  const [products, setProducts] = useState([]);
  // Products this user may map WhatsApp groups to — null = all (Super Admin).
  const [waProductIds, setWaProductIds] = useState(null);
  // Admins, plus product managers of any role (limited to their own products' groups).
  const [canMapWhatsApp, setCanMapWhatsApp] = useState(false);
  // Products this user may map mailboxes / manage widgets for — null = all (platform admin).
  const [emailProductIds, setEmailProductIds] = useState(null);
  // Admins, plus product managers (limited to their own products). Also gates
  // Client widgets and the read-only Team rules view for product managers.
  const [canMapEmail, setCanMapEmail] = useState(false);
  // Client widgets scope (managers + members an admin granted Widgets) — null = all.
  const [widgetProductIds, setWidgetProductIds] = useState(null);
  const [canManageWidgets, setCanManageWidgets] = useState(false);
  // Members granted views (Team → Access) don't manage products, so their
  // dropdowns come from the active product list filtered by their scope.
  const [activeProducts, setActiveProducts] = useState([]);
  const [productsLoaded, setProductsLoaded] = useState(false);
  // Access tab → Product modules editor for the selected member.
  const [editingAccess, setEditingAccess] = useState([]);
  const [savingAccess, setSavingAccess] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ key: '', name: '', description: '', company_id: '', handles_domains: ['product'] });
  const [companies, setCompanies] = useState([]);
  const [showCompaniesPanel, setShowCompaniesPanel] = useState(false);
  const [productClients, setProductClients] = useState([]);
  const [keyTouched, setKeyTouched] = useState(false);
  const [domainEdit, setDomainEdit] = useState([]);
  const [savingDomains, setSavingDomains] = useState(false);
  const [assignToManager, setAssignToManager] = useState(true);
  const [closureReminderEnabled, setClosureReminderEnabled] = useState(false);
  const [closureReminderNotifyManager, setClosureReminderNotifyManager] = useState(true);
  const [closureReminderInterval, setClosureReminderInterval] = useState(60);
  const [acceptTickets, setAcceptTickets] = useState(true);
  const [whatsappManualUpdates, setWhatsappManualUpdates] = useState(false);
  const [savingAssignConfig, setSavingAssignConfig] = useState(false);
  const [savingClosureReminder, setSavingClosureReminder] = useState(false);
  const [savingAcceptTickets, setSavingAcceptTickets] = useState(false);
  const [savingWaManual, setSavingWaManual] = useState(false);
  const [savingBridgeGroupId, setSavingBridgeGroupId] = useState(null);
  const [whitelistGroup, setWhitelistGroup] = useState(null); // group row being edited
  const [liveMembers, setLiveMembers] = useState([]);
  const [whitelistSelected, setWhitelistSelected] = useState(() => new Set());
  const [loadingWhitelist, setLoadingWhitelist] = useState(false);
  const [savingWhitelist, setSavingWhitelist] = useState(false);
  const [whitelistSearch, setWhitelistSearch] = useState('');
  // 'team' = ticked members never raise tickets; 'clients' = only ticked members raise tickets.
  const [memberListMode, setMemberListMode] = useState('clients');
  const [savedMemberListMode, setSavedMemberListMode] = useState('clients');
  const [editMeta, setEditMeta] = useState({ name: '', description: '', company_id: '' });
  const [savingMeta, setSavingMeta] = useState(false);
  const [permUser, setPermUser] = useState(null);
  const [permState, setPermState] = useState(null);
  const [permLoading, setPermLoading] = useState(false);
  const [permSaving, setPermSaving] = useState(false);
  const [memberForm, setMemberForm] = useState({ user: null, org_role: 'member', department_id: '', client_ids: [] });
  const [editingClientMemberId, setEditingClientMemberId] = useState(null);
  const [editingClientIds, setEditingClientIds] = useState([]);
  const [raiserForm, setRaiserForm] = useState({ user: null });
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [memberSearch, setMemberSearch] = useState('');
  const [raiserSearch, setRaiserSearch] = useState('');
  const [settingsTab, setSettingsTab] = useState('general');
  const [showAddMember, setShowAddMember] = useState(false);
  const [memberClientsOpen, setMemberClientsOpen] = useState(false);
  const [showAddRaiser, setShowAddRaiser] = useState(false);
  const [newDeptName, setNewDeptName] = useState('');
  const [renamingDeptId, setRenamingDeptId] = useState(null);
  const [renameDeptName, setRenameDeptName] = useState('');
  const [savingDept, setSavingDept] = useState(false);
  const [movingMemberId, setMovingMemberId] = useState(null);
  /** Top-level page segments — only one visible at a time (less scrolling). */
  const [pageView, setPageView] = useState('products'); // products | unmapped (admins)

  const [groups, setGroups] = useState([]);
  const [syncingGroups, setSyncingGroups] = useState(false);
  /** product_id → { loading, loaded, clients: [{value,label,parent_field,parent_value,already_mapped,...}] } */
  const [clientsByProduct, setClientsByProduct] = useState({});
  const waClientFetches = useRef({}); // product_id → Promise
  const [draftLinks, setDraftLinks] = useState({}); // group_id → { product_id, client_name }
  // Product → Team rules (per product).
  const [productRules, setProductRules] = useState({ allow: false, inherited: true });
  const [loadingTeamRules, setLoadingTeamRules] = useState(false);
  const [savingTeamRules, setSavingTeamRules] = useState(false);

  const [emails, setEmails] = useState([]);
  const [emailClientsByProduct, setEmailClientsByProduct] = useState({});
  const emailClientFetches = useRef({}); // product_id → Promise
  const [emailDraftLinks, setEmailDraftLinks] = useState({}); // email_id → { product_id, client_name, routing_mode }
  const [showAddEmail, setShowAddEmail] = useState(false);
  const [editingEmail, setEditingEmail] = useState(null);
  const [newEmailForm, setNewEmailForm] = useState(EMPTY_EMAIL_FORM);
  const [addingEmail, setAddingEmail] = useState(false);
  const [fetchingEmailId, setFetchingEmailId] = useState(null);

  const load = () => axios.get(API, authHeaders()).then((r) => setProducts(r.data)).catch(() => setProducts([]))
    .finally(() => setProductsLoaded(true));
  const loadWidgetScope = () => axios.get(apiPath('/api/client-widgets/scope'), authHeaders())
    .then((r) => {
      setCanManageWidgets(!!r.data?.can_manage);
      setWidgetProductIds(r.data?.all ? null : new Set(r.data?.product_ids || []));
    })
    .catch(() => setCanManageWidgets(false));
  const loadActiveProducts = () => axios.get(`${API}/active`, authHeaders())
    .then((r) => setActiveProducts(Array.isArray(r.data) ? r.data : []))
    .catch(() => setActiveProducts([]));
  const loadCompanies = () => axios.get(apiPath('/api/companies'), authHeaders())
    .then((r) => setCompanies(r.data)).catch(() => setCompanies([]));
  const loadGroups = () => axios.get(`${WA}/groups`, authHeaders()).then((r) => {
    const rows = r.data || [];
    setGroups(rows);
    const drafts = {};
    rows.forEach((g) => {
      drafts[g.group_id] = {
        product_id: g.product_id || '',
        client_name: g.client_name || '',
      };
    });
    setDraftLinks(drafts);
  }).catch(() => setGroups([]));
  const loadWaScope = () => axios.get(`${WA}/groups/scope`, authHeaders())
    .then((r) => {
      const canMap = !!r.data?.can_map;
      setCanMapWhatsApp(canMap);
      setWaProductIds(r.data?.all ? null : new Set(r.data?.product_ids || []));
      if (canMap) loadGroups();
    })
    .catch(() => setCanMapWhatsApp(false));
  const loadTeamRules = (productId) => {
    setLoadingTeamRules(true);
    return axios.get(`${API}/${productId}/team-rules`, authHeaders())
      .then((r) => setProductRules({
        allow: !!r.data.allow_multi_product_roles,
        inherited: !!r.data.inherited,
      }))
      .catch(() => setProductRules({ allow: false, inherited: true }))
      .finally(() => setLoadingTeamRules(false));
  };
  const loadEmailScope = () => axios.get(`${EMAIL_MAPPING}/scope`, authHeaders())
    .then((r) => {
      const canMap = !!r.data?.can_map;
      setCanMapEmail(canMap);
      setEmailProductIds(r.data?.all ? null : new Set(r.data?.product_ids || []));
      if (canMap) loadEmails();
    })
    .catch(() => setCanMapEmail(false));
  const loadEmails = () => axios.get(EMAIL_MAPPING, authHeaders()).then((r) => {
    const rows = r.data || [];
    setEmails(rows);
    const drafts = {};
    rows.forEach((e) => {
      drafts[e.email_id] = {
        product_id: e.product_id || '',
        client_name: e.client_name || '',
        routing_mode: e.routing_mode || 'single',
      };
    });
    setEmailDraftLinks(drafts);
  }).catch(() => setEmails([]));

  useEffect(() => {
    load();
    loadCompanies();
    // Product managers get the mailboxes mapped to their own products.
    loadEmailScope();
    loadWaScope();
    loadWidgetScope();
    if (!isPlatformAdmin) loadActiveProducts();
  }, [isPlatformAdmin]);

  // Products the Unmapped view may map to: own products, plus (for members an
  // admin granted access) active products — each table filters by its own scope.
  const mappableProducts = useMemo(() => {
    if (isPlatformAdmin) return products;
    const seen = new Set(products.map((p) => p.product_id));
    return [...products, ...activeProducts.filter((p) => !seen.has(p.product_id))];
  }, [isPlatformAdmin, products, activeProducts]);
  useEffect(() => {
    if (!isPlatformAdmin && pageView === 'unmapped') setPageView('products');
  }, [isPlatformAdmin, pageView]);

  // Tabs this user may open on a product: modules from the product's
  // ``my_access`` (all for admins / its manager), and for the mapping tabs also
  // the matching mapping scope (e.g. WhatsApp is Super Admin or product-scoped).
  const inScope = (ids, productId) => ids === null || (ids && ids.has(productId));
  const tabsFor = (product) => {
    if (!product) return [];
    const mods = new Set(product.my_access?.modules || []);
    const pid = product.product_id;
    return PRODUCT_TABS.filter((t) => {
      if (!mods.has(t.module)) return false;
      if (t.id === 'whatsapp') return canMapWhatsApp && inScope(waProductIds, pid);
      if (t.id === 'email') return canMapEmail && inScope(emailProductIds, pid);
      if (t.id === 'widgets') return canManageWidgets && inScope(widgetProductIds, pid);
      return true;
    });
  };

  // Prefetch the selected product's client list for its WhatsApp / Email tabs
  // (always refresh after a save — "already mapped" flags change).
  useEffect(() => {
    if (pageView !== 'products' || !selected?.product_id) return;
    if (settingsTab === 'whatsapp') ensureClientsLoaded(selected.product_id, { force: true });
    if (settingsTab === 'email') ensureEmailClientsLoaded(selected.product_id, { force: true });
    if (settingsTab === 'team_rules') loadTeamRules(selected.product_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageView, settingsTab, selected?.product_id, groups, emails]);

  const ensureClientsLoaded = async (productId, { force = false } = {}) => {
    if (!productId) return [];
    if (!force && clientsByProduct[productId]?.loaded) {
      return clientsByProduct[productId].clients || [];
    }
    if (!force && waClientFetches.current[productId]) {
      return waClientFetches.current[productId];
    }
    setClientsByProduct((prev) => ({
      ...prev,
      [productId]: { loading: true, loaded: false, clients: prev[productId]?.clients || [] },
    }));
    const req = axios
      .get(`${WA}/products/${encodeURIComponent(productId)}/clients`, authHeaders())
      .then((res) => {
        const clients = res.data?.clients || [];
        setClientsByProduct((prev) => ({
          ...prev,
          [productId]: { loading: false, loaded: true, clients },
        }));
        return clients;
      })
      .catch(() => {
        setClientsByProduct((prev) => ({
          ...prev,
          [productId]: { loading: false, loaded: true, clients: [] },
        }));
        return [];
      })
      .finally(() => {
        delete waClientFetches.current[productId];
      });
    waClientFetches.current[productId] = req;
    return req;
  };

  const ensureEmailClientsLoaded = async (productId, { force = false } = {}) => {
    if (!productId) return [];
    if (!force && emailClientsByProduct[productId]?.loaded) {
      return emailClientsByProduct[productId].clients || [];
    }
    if (!force && emailClientFetches.current[productId]) {
      return emailClientFetches.current[productId];
    }
    setEmailClientsByProduct((prev) => ({
      ...prev,
      [productId]: { loading: true, loaded: false, clients: prev[productId]?.clients || [] },
    }));
    const req = axios
      .get(`${EMAIL_MAPPING}/products/${encodeURIComponent(productId)}/clients`, authHeaders())
      .then((res) => {
        const clients = res.data?.clients || [];
        setEmailClientsByProduct((prev) => ({
          ...prev,
          [productId]: { loading: false, loaded: true, clients },
        }));
        return clients;
      })
      .catch(() => {
        setEmailClientsByProduct((prev) => ({
          ...prev,
          [productId]: { loading: false, loaded: true, clients: [] },
        }));
        return [];
      })
      .finally(() => {
        delete emailClientFetches.current[productId];
      });
    emailClientFetches.current[productId] = req;
    return req;
  };

  const saveTeamRules = async () => {
    if (!selected) return;
    setSavingTeamRules(true);
    setError(null);
    try {
      const res = await axios.patch(`${API}/${selected.product_id}/team-rules`, {
        allow_multi_product_roles: productRules.allow,
      }, authHeaders());
      setProductRules({ allow: !!res.data.allow_multi_product_roles, inherited: false });
      notifySuccess(`Team rules saved for ${selected.name}`);
    } catch (err) {
      notifyError(err.response?.data?.detail || 'Failed to save team rules');
      setError(err.response?.data?.detail || 'Failed to save team rules');
    } finally {
      setSavingTeamRules(false);
    }
  };

  const setDraftLink = (groupId, patch) => {
    setDraftLinks((prev) => ({
      ...prev,
      [groupId]: { ...(prev[groupId] || { product_id: '', client_name: '' }), ...patch },
    }));
  };

  const syncGroupsFromBridge = async () => {
    setSyncingGroups(true);
    setError(null);
    try {
      const r = await axios.post(`${WA}/groups/sync`, {}, authHeaders());
      const { created = 0, updated = 0, bridge_groups = 0, hint } = r.data || {};
      notifySuccess(
        `Synced from live WhatsApp: ${bridge_groups} group(s) on bridge`
        + ` · ${created} new · ${updated} refreshed`
        + (hint ? `. ${hint}` : ''),
      );
      await loadGroups();
    } catch (err) {
      notifyError(err.response?.data?.detail || 'Failed to sync groups from WhatsApp bridge');
      setError(err.response?.data?.detail || 'Failed to sync groups from WhatsApp bridge');
    } finally {
      setSyncingGroups(false);
    }
  };

  const saveGroupLink = async (groupId) => {
    const draft = draftLinks[groupId] || {};
    const productId = draft.product_id || '';
    const clientName = draft.client_name || '';
    setError(null);
    try {
      if (!productId) {
        await axios.post(`${WA}/groups/${encodeURIComponent(groupId)}/unlink`, {}, authHeaders());
        notifySuccess('Group unlinked');
      } else {
        const clients = await ensureClientsLoaded(productId);
        const match = clients.find((c) => c.value === clientName);
        if (clients.length > 0 && !clientName) {
          notifyError('Select a client for this product (one primary group per client)');
          return;
        }
        const payload = {
          product_id: productId,
          client_name: clientName || null,
          client_type: match?.parent_field === 'client_type' ? match.parent_value : null,
          client_group: match?.parent_field === 'client_group' ? match.parent_value : null,
        };
        const r = await axios.post(
          `${WA}/groups/${encodeURIComponent(groupId)}/link`,
          payload,
          authHeaders(),
        );
        notifySuccess(
          `Linked${r.data.client_name ? ` → ${r.data.client_name}` : ''}`
          + `${r.data.backfilled ? ` · ${r.data.backfilled} past ticket(s) routed` : ''}`,
        );
      }
      await loadGroups();
      // Refresh client "already mapped" flags for this product
      if (productId) {
        setClientsByProduct((prev) => {
          const next = { ...prev };
          delete next[productId];
          return next;
        });
        await ensureClientsLoaded(productId);
      }
    } catch (err) {
      notifyError(err.response?.data?.detail || 'Failed to update group mapping');
      setError(err.response?.data?.detail || 'Failed to update group mapping');
    }
  };

  const setEmailDraftLink = (emailId, patch) => {
    setEmailDraftLinks((prev) => ({
      ...prev,
      [emailId]: { ...(prev[emailId] || { product_id: '', client_name: '', routing_mode: 'single' }), ...patch },
    }));
  };

  const openEmailRoutes = (emailId) => navigate(`/dashboard/products/email/${encodeURIComponent(emailId)}/mapping`);

  const openAddEmail = () => {
    setEditingEmail(null);
    setNewEmailForm(EMPTY_EMAIL_FORM);
    setError(null);
    setShowAddEmail(true);
  };

  const openEditEmail = (row) => {
    setEditingEmail(row);
    setNewEmailForm({
      email_address: row.email_address,
      label: row.label || '',
      imap_host: row.imap_host || '',
      imap_port: row.imap_port || '993',
      imap_username: row.imap_username || '',
      imap_password: row.imap_password || '',
      imap_folder: row.imap_folder || 'INBOX',
      imap_use_ssl: row.imap_use_ssl !== false,
    });
    setError(null);
    setShowAddEmail(true);
  };

  const saveEmailForm = async (e) => {
    e.preventDefault();
    const email_address = newEmailForm.email_address.trim();
    if (!email_address) return;
    setAddingEmail(true);
    setError(null);
    const payload = {
      email_address,
      label: newEmailForm.label.trim() || null,
      imap_host: newEmailForm.imap_host.trim(),
      imap_port: newEmailForm.imap_port ? Number(newEmailForm.imap_port) : 993,
      imap_username: newEmailForm.imap_username.trim() || email_address,
      imap_password: newEmailForm.imap_password,
      imap_folder: newEmailForm.imap_folder.trim() || 'INBOX',
      imap_use_ssl: newEmailForm.imap_use_ssl,
    };
    try {
      if (editingEmail) {
        await axios.put(`${EMAIL_MAPPING}/${encodeURIComponent(editingEmail.email_id)}`, payload, authHeaders());
        notifySuccess(`Updated ${email_address}`);
      } else {
        await axios.post(EMAIL_MAPPING, payload, authHeaders());
        notifySuccess(`Added ${email_address}`);
      }
      setShowAddEmail(false);
      setEditingEmail(null);
      await loadEmails();
    } catch (err) {
      notifyError(err.response?.data?.detail || 'Failed to save mailbox');
    } finally {
      setAddingEmail(false);
    }
  };

  const removeEmail = async (row) => {
    if (!window.confirm(`Remove "${row.email_address}" from the registry?`)) return;
    setError(null);
    try {
      await axios.delete(`${EMAIL_MAPPING}/${encodeURIComponent(row.email_id)}`, authHeaders());
      notifySuccess('Email removed');
      await loadEmails();
    } catch (err) {
      notifyError(err.response?.data?.detail || 'Failed to remove email');
    }
  };

  const fetchEmailNow = async (row) => {
    setFetchingEmailId(row.email_id);
    setError(null);
    try {
      const r = await axios.post(`${EMAIL_MAPPING}/${encodeURIComponent(row.email_id)}/fetch`, {}, authHeaders());
      if (r.data.ok) {
        notifySuccess(`${row.email_address}: fetched ${r.data.fetched}, created ${r.data.created} ticket(s)`);
      } else {
        notifyError(`${row.email_address}: ${r.data.error || 'Fetch failed'}`);
      }
      await loadEmails();
    } catch (err) {
      notifyError(err.response?.data?.detail || 'Failed to fetch mailbox');
    } finally {
      setFetchingEmailId(null);
    }
  };

  const toggleEmailEnabled = async (row) => {
    const stopping = row.enabled !== false;
    setError(null);
    try {
      await axios.post(
        `${EMAIL_MAPPING}/${encodeURIComponent(row.email_id)}/${stopping ? 'stop' : 'start'}`,
        {},
        authHeaders(),
      );
      notifySuccess(stopping ? `Stopped ${row.email_address} — no fetching or tickets until started again` : `Started ${row.email_address}`);
      await loadEmails();
    } catch (err) {
      notifyError(err.response?.data?.detail || `Failed to ${stopping ? 'stop' : 'start'} mailbox`);
    }
  };

  const saveEmailLink = async (emailId) => {
    const draft = emailDraftLinks[emailId] || {};
    const productId = draft.product_id || '';
    const clientName = draft.client_name || '';
    const routingMode = draft.routing_mode || 'single';
    setError(null);
    try {
      if (!productId) {
        await axios.post(`${EMAIL_MAPPING}/${encodeURIComponent(emailId)}/unlink`, {}, authHeaders());
        notifySuccess('Email unlinked');
      } else {
        const clients = await ensureEmailClientsLoaded(productId);
        if (routingMode === 'single' && clients.length > 0 && !clientName) {
          notifyError('Pick the client for this mailbox, or switch it to "Shared inbox"');
          return;
        }
        const match = clients.find((c) => c.value === clientName);
        const single = routingMode === 'single';
        const payload = {
          product_id: productId,
          routing_mode: routingMode,
          client_name: single ? (clientName || null) : null,
          client_type: single && match?.parent_field === 'client_type' ? match.parent_value : null,
          client_group: single && match?.parent_field === 'client_group' ? match.parent_value : null,
        };
        const r = await axios.post(
          `${EMAIL_MAPPING}/${encodeURIComponent(emailId)}/link`,
          payload,
          authHeaders(),
        );
        notifySuccess(
          r.data.routing_mode === 'shared'
            ? 'Saved as shared inbox — now map senders to clients'
            : `Linked${r.data.client_name ? ` → ${r.data.client_name}` : ''}`,
        );
      }
      await loadEmails();
    } catch (err) {
      notifyError(err.response?.data?.detail || 'Failed to update email mapping');
    }
  };

  const openProduct = async (id) => {
    const sameProduct = selected?.product_id === id;
    setError(null);
    if (!sameProduct) {
      setPermUser(null);
      setPermState(null);
      setShowAddMember(false);
      setMemberSearch('');
    }
    let res;
    try {
      res = await axios.get(`${API}/${id}`, authHeaders());
    } catch (err) {
      notifyError(err.response?.data?.detail || 'Failed to open product');
      return;
    }
    setSelected(res.data);
    const tabs = tabsFor(res.data);
    // Stay on the current tab when reloading the same product; otherwise open
    // the first tab this user is allowed to see.
    setSettingsTab((cur) => (sameProduct && tabs.some((t) => t.id === cur) ? cur : (tabs[0]?.id || 'general')));
    if (tabs.some((t) => t.id === 'team')) {
      axios.get(`${API}/${id}/clients`, authHeaders())
        .then((r) => setProductClients(r.data))
        .catch(() => setProductClients([]));
    } else {
      setProductClients([]);
    }
    setDomainEdit(res.data.handles_domains || ['product']);
    setAssignToManager(res.data.assign_to_manager_on_create !== false);
    setClosureReminderEnabled(res.data.closure_reminder_enabled === true);
    setClosureReminderNotifyManager(res.data.closure_reminder_notify_manager !== false);
    setClosureReminderInterval(
      Number(res.data.closure_reminder_interval_minutes) > 0
        ? Number(res.data.closure_reminder_interval_minutes)
        : 60,
    );
    setAcceptTickets(res.data.accept_tickets !== false);
    setWhatsappManualUpdates(res.data.whatsapp_manual_updates === true);
    setEditMeta({ name: res.data.name || '', description: res.data.description || '', company_id: res.data.company_id || '' });
    setShowCreate(false);
  };

  const saveProductMeta = async () => {
    if (!selected) return;
    const name = editMeta.name.trim();
    if (!name) {
      notifyError('Product name is required');
      setError('Product name is required');
      return;
    }
    setSavingMeta(true);
    setError(null);
    try {
      await axios.patch(`${API}/${selected.product_id}`, {
        name,
        description: editMeta.description.trim() || null,
        company_id: editMeta.company_id || null,
      }, authHeaders());
      notifySuccess('Product details updated');
      await openProduct(selected.product_id);
      await load();
    } catch (err) {
      notifyError(err.response?.data?.detail || 'Failed to update product');
      setError(err.response?.data?.detail || 'Failed to update product');
    } finally {
      setSavingMeta(false);
    }
  };

  const openTicketPerms = async (member) => {
    if (!selected) return;
    setPermUser(member);
    setEditingAccess([...(member.feature_access || [])]);
    setPermLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API}/${selected.product_id}/team/members/${member.user_id}/ticket-permissions`,
        authHeaders(),
      );
      setPermState(res.data);
    } catch (err) {
      notifyError(err.response?.data?.detail || 'Failed to load ticket permissions');
      setError(err.response?.data?.detail || 'Failed to load ticket permissions');
      setPermUser(null);
      setPermState(null);
    } finally {
      setPermLoading(false);
    }
  };

  const togglePerm = (key) => {
    setPermState((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        permissions: { ...prev.permissions, [key]: !prev.permissions[key] },
      };
    });
  };

  const saveTicketPerms = async () => {
    if (!selected || !permUser || !permState) return;
    setPermSaving(true);
    setError(null);
    try {
      const res = await axios.put(
        `${API}/${selected.product_id}/team/members/${permUser.user_id}/ticket-permissions`,
        permState.permissions,
        authHeaders(),
      );
      setPermState(res.data);
      notifySuccess(`Ticket permissions saved for ${permUser.name} on ${selected.name} only`);
    } catch (err) {
      notifyError(err.response?.data?.detail || 'Failed to save ticket permissions');
      setError(err.response?.data?.detail || 'Failed to save ticket permissions');
    } finally {
      setPermSaving(false);
    }
  };

  const resetTicketPerms = async () => {
    if (!selected || !permUser) return;
    if (!window.confirm('Reset to role defaults for this product? Extra grants will be removed.')) return;
    setPermSaving(true);
    setError(null);
    try {
      const res = await axios.delete(
        `${API}/${selected.product_id}/team/members/${permUser.user_id}/ticket-permissions`,
        authHeaders(),
      );
      setPermState(res.data);
      notifySuccess('Ticket permissions reset to role defaults');
    } catch (err) {
      notifyError(err.response?.data?.detail || 'Failed to reset permissions');
      setError(err.response?.data?.detail || 'Failed to reset permissions');
    } finally {
      setPermSaving(false);
    }
  };

  const saveAssignConfig = async () => {
    if (!selected) return;
    setSavingAssignConfig(true);
    setError(null);
    try {
      await axios.patch(
        `${API}/${selected.product_id}`,
        { assign_to_manager_on_create: assignToManager },
        authHeaders(),
      );
      notifySuccess(assignToManager
        ? 'New tickets will enter the manager queue (unassigned until claimed or assigned)'
        : 'New tickets will stay unassigned for the team to pick up');
      await openProduct(selected.product_id);
    } catch (err) {
      notifyError(err.response?.data?.detail || 'Failed to save assignment config');
      setError(err.response?.data?.detail || 'Failed to save assignment config');
    } finally {
      setSavingAssignConfig(false);
    }
  };

  const saveClosureReminderConfig = async () => {
    if (!selected) return;
    const interval = Math.max(15, parseInt(closureReminderInterval, 10) || 60);
    setClosureReminderInterval(interval);
    setSavingClosureReminder(true);
    setError(null);
    try {
      await axios.patch(
        `${API}/${selected.product_id}`,
        {
          closure_reminder_enabled: closureReminderEnabled,
          closure_reminder_notify_manager: closureReminderNotifyManager,
          closure_reminder_interval_minutes: interval,
        },
        authHeaders(),
      );
      notifySuccess(
        closureReminderEnabled
          ? `Closure reminders on — every ${interval} min to assignees`
            + (closureReminderNotifyManager ? ' and product manager' : '')
          : 'Closure reminders turned off for this product',
      );
      await openProduct(selected.product_id);
    } catch (err) {
      notifyError(err.response?.data?.detail || 'Failed to save closure reminder settings');
      setError(err.response?.data?.detail || 'Failed to save closure reminder settings');
    } finally {
      setSavingClosureReminder(false);
    }
  };

  const saveAcceptTickets = async (next) => {
    if (!selected) return;
    const previous = acceptTickets;
    setAcceptTickets(next);
    setSavingAcceptTickets(true);
    setError(null);
    try {
      await axios.patch(
        `${API}/${selected.product_id}`,
        { accept_tickets: next },
        authHeaders(),
      );
      notifySuccess(next
        ? 'This product is accepting new tickets'
        : 'This product is hidden/grayed out when raising tickets');
      await openProduct(selected.product_id);
    } catch (err) {
      setAcceptTickets(previous);
      notifyError(err.response?.data?.detail || 'Failed to update accept tickets setting');
      setError(err.response?.data?.detail || 'Failed to update accept tickets setting');
    } finally {
      setSavingAcceptTickets(false);
    }
  };

  const saveWhatsappManualUpdates = async (next) => {
    if (!selected) return;
    const previous = whatsappManualUpdates;
    setWhatsappManualUpdates(next);
    setSavingWaManual(true);
    setError(null);
    try {
      await axios.patch(
        `${API}/${selected.product_id}`,
        { whatsapp_manual_updates: next },
        authHeaders(),
      );
      notifySuccess(next
        ? 'Manual tickets will post updates to the mapped WhatsApp group'
        : 'WhatsApp updates for manually created tickets are off');
      await openProduct(selected.product_id);
    } catch (err) {
      setWhatsappManualUpdates(previous);
      notifyError(err.response?.data?.detail || 'Failed to update WhatsApp manual updates setting');
      setError(err.response?.data?.detail || 'Failed to update WhatsApp manual updates setting');
    } finally {
      setSavingWaManual(false);
    }
  };

  const toggleGroupBridge = async (groupId, nextEnabled) => {
    setSavingBridgeGroupId(groupId);
    setError(null);
    try {
      await axios.patch(
        `${WA}/groups/${encodeURIComponent(groupId)}/bridge`,
        { bridge_enabled: nextEnabled },
        authHeaders(),
      );
      notifySuccess(nextEnabled
        ? 'WhatsApp bridge started for this group (auto tickets + replies)'
        : 'WhatsApp bridge stopped for this group (manual ticket updates still allowed)');
      await loadGroups();
    } catch (err) {
      notifyError(err.response?.data?.detail || 'Failed to update group bridge setting');
      setError(err.response?.data?.detail || 'Failed to update group bridge setting');
    } finally {
      setSavingBridgeGroupId(null);
    }
  };

  const closeWhitelistModal = () => {
    setWhitelistGroup(null);
    setLiveMembers([]);
    setWhitelistSelected(new Set());
    setWhitelistSearch('');
    setLoadingWhitelist(false);
    setSavingWhitelist(false);
    setMemberListMode('team');
    setSavedMemberListMode('team');
  };

  const openWhitelistModal = async (group) => {
    setWhitelistGroup(group);
    setLiveMembers([]);
    setWhitelistSelected(new Set());
    setWhitelistSearch('');
    setLoadingWhitelist(true);
    setError(null);
    try {
      const r = await axios.get(
        `${WA}/group-live-members`,
        { ...authHeaders(), params: { group_id: group.group_id } },
      );
      const members = r.data?.members || [];
      // Nothing saved yet → default the popup to "Ticked = clients" (tick a person to let them raise tickets).
      const hasSavedList = (r.data?.whitelist_count || 0) > 0;
      const mode = !hasSavedList || r.data?.member_list_mode === 'clients' ? 'clients' : 'team';
      setLiveMembers(members);
      setMemberListMode(mode);
      setSavedMemberListMode(mode);
      setWhitelistSelected(new Set(members.filter((m) => m.whitelisted).map((m) => m.match_key)));
    } catch (err) {
      notifyError(err.response?.data?.detail || 'Failed to fetch WhatsApp group members');
      setError(err.response?.data?.detail || 'Failed to fetch WhatsApp group members');
      closeWhitelistModal();
    } finally {
      setLoadingWhitelist(false);
    }
  };

  const changeMemberListMode = (mode) => {
    if (mode === memberListMode) return;
    setMemberListMode(mode);
    // Ticks mean the opposite in the other mode — start fresh, or restore the saved ticks.
    setWhitelistSelected(
      mode === savedMemberListMode
        ? new Set(liveMembers.filter((m) => m.whitelisted).map((m) => m.match_key))
        : new Set(),
    );
  };

  const toggleWhitelistMember = (matchKey) => {
    setWhitelistSelected((prev) => {
      const next = new Set(prev);
      if (next.has(matchKey)) next.delete(matchKey);
      else next.add(matchKey);
      return next;
    });
  };

  const saveGroupWhitelist = async () => {
    if (!whitelistGroup) return;
    setSavingWhitelist(true);
    setError(null);
    try {
      const members = liveMembers
        .filter((m) => whitelistSelected.has(m.match_key))
        .map((m) => ({
          match_key: m.match_key,
          phone: m.phone,
          jid: m.jid,
          name: m.name || null,
        }));
      const r = await axios.put(
        `${WA}/groups/${encodeURIComponent(whitelistGroup.group_id)}/whitelist`,
        { members, mode: memberListMode },
        authHeaders(),
      );
      notifySuccess(
        memberListMode === 'clients'
          ? `Saved ${r.data?.count || 0} client(s) — only their messages create tickets`
          : `Whitelisted ${r.data?.count || 0} team member(s) — their messages skip LLM and tickets`,
      );
      closeWhitelistModal();
      await loadGroups();
    } catch (err) {
      notifyError(err.response?.data?.detail || 'Failed to save team whitelist');
      setError(err.response?.data?.detail || 'Failed to save team whitelist');
    } finally {
      setSavingWhitelist(false);
    }
  };

  const saveDomains = async () => {
    if (!selected) return;
    setSavingDomains(true);
    setError(null);
    try {
      await axios.patch(`${API}/${selected.product_id}`, { handles_domains: domainEdit }, authHeaders());
      notifySuccess('Ticket routing updated');
      await openProduct(selected.product_id);
      await load();
    } catch (err) {
      notifyError(err.response?.data?.detail || 'Failed to update routing');
      setError(err.response?.data?.detail || 'Failed to update routing');
    } finally {
      setSavingDomains(false);
    }
  };

  const onNameChange = (name) => {
    setForm((f) => ({
      ...f,
      name,
      key: keyTouched ? f.key : slugFromName(name),
    }));
  };

  const createProduct = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        key: form.key || slugFromName(form.name),
        name: form.name.trim(),
        description: form.description.trim() || null,
        company_id: form.company_id || null,
        handles_domains: form.handles_domains.length ? form.handles_domains : ['product'],
      };
      const res = await axios.post(API, payload, authHeaders());
      setForm({ key: '', name: '', description: '', company_id: '', handles_domains: ['product'] });
      setKeyTouched(false);
      setShowCreate(false);
      notifySuccess(`Product "${res.data.name}" created`);
      await load();
      openProduct(res.data.product_id);
    } catch (err) {
      notifyError(err.response?.data?.detail || 'Failed to create product');
      setError(err.response?.data?.detail || 'Failed to create product');
    } finally {
      setSubmitting(false);
    }
  };

  const addMember = async () => {
    if (!selected || !memberForm.user) return;
    setError(null);
    try {
      const body = {
        user_id: memberForm.user.user_id,
        org_role: memberForm.org_role,
      };
      if (memberForm.department_id) {
        body.department_id = memberForm.department_id;
      }
      if (memberForm.client_ids.length) {
        body.client_ids = memberForm.client_ids;
      }
      await axios.post(`${API}/${selected.product_id}/team/members`, body, authHeaders());
      notifySuccess('Team member added');
      setMemberForm({ user: null, org_role: 'member', department_id: '', client_ids: [] });
      setShowAddMember(false);
      await openProduct(selected.product_id);
      setSettingsTab('team');
    } catch (err) {
      notifyError(err.response?.data?.detail || 'Failed to add team member');
      setError(err.response?.data?.detail || 'Failed to add team member');
    }
  };

  const createDepartment = async () => {
    if (!selected) return;
    const name = newDeptName.trim();
    if (!name) return;
    setSavingDept(true);
    setError(null);
    try {
      await axios.post(
        `${API}/${selected.product_id}/departments`,
        { name },
        authHeaders(),
      );
      notifySuccess(`Department "${name}" created`);
      setNewDeptName('');
      await openProduct(selected.product_id);
      setSettingsTab('team');
    } catch (err) {
      notifyError(err.response?.data?.detail || 'Failed to create department');
      setError(err.response?.data?.detail || 'Failed to create department');
    } finally {
      setSavingDept(false);
    }
  };

  const saveRenameDepartment = async () => {
    if (!selected || !renamingDeptId) return;
    const name = renameDeptName.trim();
    if (!name) return;
    setSavingDept(true);
    setError(null);
    try {
      await axios.patch(
        `${API}/${selected.product_id}/departments/${renamingDeptId}`,
        { name },
        authHeaders(),
      );
      notifySuccess('Department renamed');
      setRenamingDeptId(null);
      setRenameDeptName('');
      await openProduct(selected.product_id);
      setSettingsTab('team');
    } catch (err) {
      notifyError(err.response?.data?.detail || 'Failed to rename department');
      setError(err.response?.data?.detail || 'Failed to rename department');
    } finally {
      setSavingDept(false);
    }
  };

  const deleteDepartment = async (dept) => {
    if (!selected || !dept?.id) return;
    if (!window.confirm(
      `Delete department "${dept.name}"?\n\nMembers must be moved or unassigned first.`,
    )) return;
    setSavingDept(true);
    setError(null);
    try {
      await axios.delete(
        `${API}/${selected.product_id}/departments/${dept.id}`,
        authHeaders(),
      );
      notifySuccess(`Department "${dept.name}" deleted`);
      if (renamingDeptId === dept.id) {
        setRenamingDeptId(null);
        setRenameDeptName('');
      }
      await openProduct(selected.product_id);
      setSettingsTab('team');
    } catch (err) {
      notifyError(err.response?.data?.detail || 'Failed to delete department');
      setError(err.response?.data?.detail || 'Failed to delete department');
    } finally {
      setSavingDept(false);
    }
  };

  const moveMemberDepartment = async (userId, departmentId) => {
    if (!selected || !userId) return;
    setMovingMemberId(userId);
    setError(null);
    try {
      const body = departmentId
        ? { department_id: departmentId }
        : { clear_department: true };
      await axios.patch(
        `${API}/${selected.product_id}/team/members/${userId}`,
        body,
        authHeaders(),
      );
      notifySuccess(departmentId ? 'Member moved' : 'Member unassigned');
      await openProduct(selected.product_id);
      setSettingsTab('team');
    } catch (err) {
      notifyError(err.response?.data?.detail || 'Failed to move member');
      setError(err.response?.data?.detail || 'Failed to move member');
    } finally {
      setMovingMemberId(null);
    }
  };

  const openClientEditorPM = (member) => {
    setError(null);
    setEditingClientMemberId(member.user_id);
    setEditingClientIds(member.client_ids || []);
  };

  const toggleEditingClientIdPM = (clientId) => {
    setEditingClientIds((prev) => (
      prev.includes(clientId) ? prev.filter((id) => id !== clientId) : [...prev, clientId]
    ));
  };

  const saveMemberClientsPM = async (userId) => {
    if (!selected || !userId) return;
    setMovingMemberId(userId);
    setError(null);
    try {
      await axios.patch(
        `${API}/${selected.product_id}/team/members/${userId}`,
        { client_ids: editingClientIds },
        authHeaders(),
      );
      notifySuccess('Member client tags updated');
      setEditingClientMemberId(null);
      await openProduct(selected.product_id);
      setSettingsTab('team');
    } catch (err) {
      notifyError(err.response?.data?.detail || 'Failed to update client tags');
      setError(err.response?.data?.detail || 'Failed to update client tags');
    } finally {
      setMovingMemberId(null);
    }
  };

  const addRaiser = async () => {
    if (!selected || !raiserForm.user) return;
    setError(null);
    try {
      await axios.post(`${API}/${selected.product_id}/team/members`, {
        user_id: raiserForm.user.user_id,
        org_role: 'raiser',
      }, authHeaders());
      notifySuccess('Ticket raiser added');
      setRaiserForm({ user: null });
      setShowAddRaiser(false);
      await openProduct(selected.product_id);
      setSettingsTab('team');
    } catch (err) {
      notifyError(err.response?.data?.detail || 'Failed to add ticket raiser');
      setError(err.response?.data?.detail || 'Failed to add ticket raiser');
    }
  };

  const deleteProduct = async () => {
    if (!selected) return;
    if (!window.confirm(
      `Delete product "${selected.name}"?\n\n`
      + 'This removes its team roster, form templates and WhatsApp mappings. '
      + 'It is blocked if open tickets are still routed to it. This cannot be undone.',
    )) return;
    setError(null);
    try {
      await axios.delete(`${API}/${selected.product_id}`, authHeaders());
      notifySuccess(`Product "${selected.name}" deleted`);
      setSelected(null);
      await load();
    } catch (err) {
      notifyError(err.response?.data?.detail || 'Failed to delete product');
      setError(err.response?.data?.detail || 'Failed to delete product');
    }
  };

  const removeMember = async (userId, kind = 'member') => {
    const msg = kind === 'raiser'
      ? 'Remove this user’s product raise access?'
      : 'Remove this member from the product team?';
    if (!window.confirm(msg)) return;
    try {
      await axios.delete(`${API}/${selected.product_id}/team/members/${userId}`, authHeaders());
      notifySuccess(kind === 'raiser' ? 'Ticket raiser removed' : 'Member removed');
      await openProduct(selected.product_id);
    } catch (err) {
      notifyError(err.response?.data?.detail || 'Failed to remove member');
      setError(err.response?.data?.detail || 'Failed to remove member');
    }
  };

  const filteredProducts = useMemo(
    () => filterBySearch(products, productSearch, PRODUCT_SEARCH_KEYS),
    [products, productSearch],
  );
  const staffMembers = useMemo(
    () => (selected?.team_members || []).filter((m) => m.org_role !== 'raiser'),
    [selected],
  );
  const raiserMembers = useMemo(
    () => (selected?.team_members || []).filter((m) => m.org_role === 'raiser'),
    [selected],
  );
  const filteredTeamMembers = useMemo(
    () => filterBySearch(staffMembers, memberSearch, TEAM_MEMBER_SEARCH_KEYS),
    [staffMembers, memberSearch],
  );
  const productDepartments = useMemo(
    () => selected?.departments || [],
    [selected],
  );
  const staffByDepartment = useMemo(() => {
    const groups = productDepartments.map((d) => ({
      id: d.id,
      name: d.name,
      members: [],
    }));
    const unassigned = { id: null, name: 'Unassigned', members: [] };
    const byId = Object.fromEntries(groups.map((g) => [g.id, g]));
    for (const m of filteredTeamMembers) {
      const deptId = m.department_id || null;
      if (deptId && byId[deptId]) {
        byId[deptId].members.push(m);
      } else {
        unassigned.members.push(m);
      }
    }
    return [...groups, unassigned];
  }, [filteredTeamMembers, productDepartments]);
  const filteredRaisers = useMemo(
    () => filterBySearch(raiserMembers, raiserSearch, TEAM_MEMBER_SEARCH_KEYS),
    [raiserMembers, raiserSearch],
  );

  const saveMemberAccess = async () => {
    if (!selected || !permUser) return;
    setSavingAccess(true);
    try {
      const res = await axios.patch(
        `${API}/${selected.product_id}/team/members/${permUser.user_id}/feature-access`,
        { features: editingAccess },
        authHeaders(),
      );
      setSelected(res.data);
      const row = (res.data.team_members || []).find((m) => m.user_id === permUser.user_id);
      if (row) {
        setPermUser(row);
        setEditingAccess([...(row.feature_access || [])]);
      }
      notifySuccess(`Product modules saved for ${permUser.name} on ${selected.name}`);
    } catch (err) {
      notifyError(err.response?.data?.detail || 'Failed to update access');
    } finally {
      setSavingAccess(false);
    }
  };

  // WhatsApp mapping table — inside a product (``scoped``: only that product's
  // groups, product fixed) or the global Unmapped view (admins).
  const renderWhatsApp = (rows, { scoped }) => {
    const needCount = rows.filter((g) => !g.mapped || (g.product_id && !g.client_name)).length;
    return (
      <div className="pm-card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px', flexShrink: 0 }}>
          <WhatsappLogo size={22} color="#25D366" />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '16px', fontWeight: 600 }}>{scoped ? `WhatsApp groups — ${selected?.name}` : 'Unmapped WhatsApp groups'}</div>
            <div style={{ fontSize: '12px', color: '#6B7280' }}>
              Product → Client → Group (one primary support group per client). Tickets auto-fill product and client.
              Click "Select members" to choose who can raise tickets: check the clients (only they raise tickets) or check our staff (they are ignored).
              {needCount > 0 && <span style={{ color: '#B45309', fontWeight: 600 }}> · {needCount} need mapping</span>}
            </div>
          </div>
          {isPlatformAdmin && !scoped && (
          <button
            type="button"
            onClick={syncGroupsFromBridge}
            disabled={syncingGroups}
            style={{ ...primaryBtn, opacity: syncingGroups ? 0.7 : 1, flexShrink: 0 }}
          >
            {syncingGroups ? 'Syncing…' : 'Sync from WhatsApp'}
        </button>
          )}
        </div>
        {isPlatformAdmin && !scoped && (
        <p style={{ color: '#6B7280', fontSize: '12px', margin: '0 0 12px' }}>
          Sync pulls every group the live linked number is in. If only a few appear, clear <code>MONITORED_GROUPS</code> on the live bridge.
        </p>
        )}
        <div>
            {rows.length === 0 ? (
              <p style={{ color: '#9CA3AF', fontSize: '12px', margin: 0 }}>
                {scoped
                  ? `No WhatsApp groups are mapped to ${selected?.name || 'this product'} yet. New groups are mapped from Product Management → Unmapped.`
                  : 'Every WhatsApp group is mapped to a product. Sync from WhatsApp to pull in new groups.'}
              </p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead style={{ position: 'sticky', top: 0, background: '#F9FAFB', zIndex: 1 }}>
                  <tr style={{ textAlign: 'left' }}>
                    <th style={th}>Group</th>
                    <th style={th}>Bridge</th>
                    <th style={th}>Team whitelist</th>
                    <th style={th}>Messages</th>
                    <th style={th}>Product</th>
                    <th style={th}>Client</th>
                    <th style={th}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((g) => {
                    const draft = draftLinks[g.group_id] || { product_id: '', client_name: '' };
                    const clientPack = clientsByProduct[draft.product_id] || {};
                    const clients = clientPack.clients || [];
                    const needsClient = !!draft.product_id && clients.length > 0;
                    const complete = !!g.product_id && (!needsClient || !!g.client_name);
                    const dirty = (draft.product_id || '') !== (g.product_id || '')
                      || (draft.client_name || '') !== (g.client_name || '');
                    const bridgeOn = g.bridge_enabled !== false;
                    const bridgeSaving = savingBridgeGroupId === g.group_id;
                    return (
                      <tr
                        key={g.group_id}
                        style={{ borderBottom: '1px solid #E5E7EB', background: complete ? 'white' : '#FFFBEB' }}
                      >
                      <td style={td}>
                        <div style={{ fontWeight: 500 }}>{g.group_name || '(unnamed)'}</div>
                        <div style={{ fontSize: '11px', color: '#9CA3AF', fontFamily: 'monospace' }}>{g.group_id}</div>
                      </td>
                        <td style={td}>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={bridgeOn}
                            aria-label={bridgeOn ? 'Stop WhatsApp bridge for group' : 'Start WhatsApp bridge for group'}
                            disabled={bridgeSaving}
                            title={bridgeOn
                              ? 'Bridge on — auto tickets + replies. Click to stop.'
                              : 'Bridge off — no auto tickets/replies. Manual ticket updates still allowed if product switch is on.'}
                            onClick={() => toggleGroupBridge(g.group_id, !bridgeOn)}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 8,
                              border: '1px solid #E5E7EB',
                              borderRadius: 8,
                              padding: '4px 8px',
                              background: bridgeOn ? '#ECFDF5' : '#F3F4F6',
                              cursor: bridgeSaving ? 'wait' : 'pointer',
                              opacity: bridgeSaving ? 0.7 : 1,
                              fontSize: 11,
                              fontWeight: 600,
                              color: bridgeOn ? '#065F46' : '#6B7280',
                            }}
                          >
                            <span
                              style={{
                                width: 34,
                                height: 20,
                                borderRadius: 999,
                                background: bridgeOn ? '#15803D' : '#D1D5DB',
                                padding: 2,
                                display: 'inline-block',
                              }}
                            >
                              <span
                                style={{
                                  display: 'block',
                                  width: 16,
                                  height: 16,
                                  borderRadius: '50%',
                                  background: '#fff',
                                  transform: bridgeOn ? 'translateX(14px)' : 'translateX(0)',
                                  transition: 'transform 0.15s ease',
                                }}
                              />
                            </span>
                            {bridgeSaving ? '…' : (bridgeOn ? 'On' : 'Off')}
                          </button>
                        </td>
                        <td style={td}>
                          <button
                            type="button"
                            onClick={() => openWhitelistModal(g)}
                            title="Choose which group members are clients or our staff"
                            style={{
                              ...secondaryBtn,
                              padding: '6px 10px',
                              fontSize: 11,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                            }}
                          >
                            <UsersThree size={14} />
                            {(g.whitelist_count || 0) > 0
                              ? (g.member_list_mode === 'clients'
                                ? `${g.whitelist_count} client(s)`
                                : `${g.whitelist_count} staff`)
                              : 'Select members'}
                          </button>
                      </td>
                      <td style={td}>{g.message_count || 0}</td>
                      <td style={td}>
                        <select
                            value={draft.product_id || ''}
                            onChange={async (e) => {
                              const productId = e.target.value;
                              setDraftLink(g.group_id, { product_id: productId, client_name: '' });
                              if (productId) await ensureClientsLoaded(productId, { force: true });
                            }}
                            style={{ ...inputStyle, minWidth: '180px', maxWidth: '240px' }}
                        >
                          <option value="">— Not mapped (Triage) —</option>
                            {(scoped ? [selected] : mappableProducts
                              .filter((p) => !waProductIds || waProductIds.has(p.product_id)))
                              .map((p) => (
                                <option key={p.product_id} value={p.product_id}>{p.name}</option>
                              ))}
                        </select>
                      </td>
                        <td style={td}>
                          {!draft.product_id ? (
                            <span style={{ color: '#9CA3AF' }}>—</span>
                          ) : clientPack.loading ? (
                            <span style={{ color: '#9CA3AF' }}>Loading clients…</span>
                          ) : clients.length === 0 ? (
                            <span style={{ color: '#9CA3AF' }}>No clients on product</span>
                          ) : (
                            <div style={{ minWidth: 220, maxWidth: 320 }} className="pm-client-ss">
                              <SearchableSelect
                                options={clientSelectOptions(clients, {
                                  currentGroupId: g.group_id,
                                  legacyValue: draft.client_name || '',
                                })}
                                value={draft.client_name || ''}
                                onChange={(v) => setDraftLink(g.group_id, { client_name: v || '' })}
                                placeholder="— Select client —"
                                searchPlaceholder="Search clients…"
                                aria-label="Client"
                              />
                            </div>
                          )}
                        </td>
                        <td style={td}>
                          <button
                            type="button"
                            className="tk-btn tk-btn-primary"
                            disabled={!dirty && complete}
                            onClick={() => saveGroupLink(g.group_id)}
                            style={{ padding: '6px 12px', fontSize: '12px' }}
                          >
                            {draft.product_id ? 'Save' : 'Unlink'}
                          </button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
        </div>
      </div>
    );
  };

  // Email mapping table — inside a product (``scoped``) or the Unmapped view.
  const renderEmail = (rows, { scoped }) => {
    const needCount = rows.filter((e) => !e.client_mapped).length;
    return (
      <div style={{ ...cardStyle, display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 210px)', minHeight: '320px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px', flexShrink: 0 }}>
          <EnvelopeSimple size={22} color="#002FA7" />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '16px', fontWeight: 600 }}>{scoped ? `Email mapping — ${selected?.name}` : 'Unmapped mailboxes'}</div>
            <div style={{ fontSize: '12px', color: '#6B7280' }}>
              One client per mailbox, or a shared inbox (e.g. support@…) where each sender decides the client.
              {needCount > 0 && <span style={{ color: '#B45309', fontWeight: 600 }}> · {needCount} need mapping</span>}
            </div>
          </div>
          {isPlatformAdmin && !scoped && (
            <button
              type="button"
              onClick={openAddEmail}
              style={primaryBtn}
            >
              <Plus size={16} weight="bold" />
              Add Email
            </button>
          )}
        </div>
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', paddingBottom: 220 }}>
            {rows.length === 0 ? (
              <p style={{ color: '#9CA3AF', fontSize: '12px', margin: 0 }}>
                {scoped
                  ? `No mailboxes are mapped to ${selected?.name || 'this product'} yet. New mailboxes are mapped from Product Management → Unmapped.`
                  : 'Every registered mailbox is mapped. Use "Add Email" to register a new one.'}
              </p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead style={{ position: 'sticky', top: 0, background: '#F9FAFB', zIndex: 1 }}>
                  <tr style={{ textAlign: 'left' }}>
                    <th style={th}>Email</th>
                    {isPlatformAdmin && <th style={th}>Mailbox</th>}
                    <th style={th}>Mode</th>
                    <th style={th}>Product</th>
                    <th style={th}>Client / senders</th>
                    {canMapEmail && <th style={th}>Action</th>}
                    {isPlatformAdmin && <th style={th} />}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((em) => {
                    const draft = emailDraftLinks[em.email_id] || { product_id: '', client_name: '', routing_mode: 'single' };
                    const clientPack = emailClientsByProduct[draft.product_id] || {};
                    const clients = clientPack.clients || [];
                    const draftShared = (draft.routing_mode || 'single') === 'shared';
                    const savedShared = (em.routing_mode || 'single') === 'shared';
                    const needsClient = !!draft.product_id && clients.length > 0 && !draftShared;
                    const complete = savedShared
                      ? !!(em.product_id && (em.route_count || 0) > 0)
                      : !!(em.product_id && (!needsClient || !!em.client_name));
                    const dirty = (draft.product_id || '') !== (em.product_id || '')
                      || (draft.client_name || '') !== (em.client_name || '')
                      || (draft.routing_mode || 'single') !== (em.routing_mode || 'single');
                    return (
                      <tr
                        key={em.email_id}
                        style={{ borderBottom: '1px solid #E5E7EB', background: complete ? 'white' : '#FFFBEB' }}
                      >
                        <td style={td}>
                          <div style={{ fontWeight: 500 }}>{em.email_address}</div>
                          {em.label && <div style={{ fontSize: '11px', color: '#9CA3AF' }}>{em.label}</div>}
                        </td>
                        {isPlatformAdmin && (
                          <td style={td}>
                            {em.enabled === false ? (
                              <span style={{ color: '#B45309', fontWeight: 600 }}>Stopped</span>
                            ) : em.last_error ? (
                              <span style={{ color: '#B91C1C' }} title={em.last_error}>Error</span>
                            ) : em.last_synced_at ? (
                              <span style={{ color: '#065F46' }}>{em.message_count || 0} msg(s)</span>
                            ) : (
                              <span style={{ color: '#9CA3AF' }}>Not synced yet</span>
                            )}
                            <div style={{ display: 'flex', gap: '8px', marginTop: '4px', flexWrap: 'wrap' }}>
                              <button type="button" onClick={() => openEditEmail(em)} style={linkAction}>Edit</button>
                              <button
                                type="button"
                                onClick={() => fetchEmailNow(em)}
                                disabled={fetchingEmailId === em.email_id || em.enabled === false}
                                style={{ ...linkAction, opacity: em.enabled === false ? 0.5 : 1 }}
                              >
                                {fetchingEmailId === em.email_id ? 'Fetching…' : 'Fetch now'}
                              </button>
                              <button
                                type="button"
                                onClick={() => toggleEmailEnabled(em)}
                                style={{ ...linkAction, color: em.enabled === false ? '#065F46' : '#B91C1C' }}
                              >
                                {em.enabled === false ? 'Start' : 'Stop'}
                              </button>
                            </div>
                          </td>
                        )}
                        <td style={td}>
                          {canMapEmail ? (
                            <select
                              value={draft.routing_mode || 'single'}
                              disabled={!draft.product_id}
                              title={!draft.product_id ? 'Map the mailbox to a product first' : undefined}
                              onChange={(e) => setEmailDraftLink(em.email_id, {
                                routing_mode: e.target.value,
                                client_name: e.target.value === 'shared' ? '' : (draft.client_name || ''),
                              })}
                              style={{ ...inputStyle, minWidth: '132px', opacity: draft.product_id ? 1 : 0.5 }}
                            >
                              <option value="single">One client</option>
                              <option value="shared">Shared inbox</option>
                            </select>
                          ) : (
                            <span style={{ fontWeight: 600 }}>{savedShared ? 'Shared inbox' : 'One client'}</span>
                          )}
                        </td>
                        <td style={td}>
                          {canMapEmail ? (
                            <select
                              value={draft.product_id || ''}
                              onChange={async (e) => {
                                const productId = e.target.value;
                                setEmailDraftLink(em.email_id, { product_id: productId, client_name: '' });
                                if (productId) await ensureEmailClientsLoaded(productId, { force: true });
                              }}
                              style={{ ...inputStyle, minWidth: '160px', maxWidth: '220px' }}
                            >
                              <option value="">— Not mapped —</option>
                              {(scoped ? [selected] : mappableProducts
                                .filter((p) => !emailProductIds || emailProductIds.has(p.product_id)))
                                .map((p) => (
                                  <option key={p.product_id} value={p.product_id}>{p.name}</option>
                                ))}
                            </select>
                          ) : (
                            <span>{em.product_name || '—'}</span>
                          )}
                        </td>
                        <td style={td}>
                          {!draft.product_id ? (
                            <span style={{ color: '#9CA3AF' }}>—</span>
                          ) : draftShared ? (
                            <div>
                              <div style={{ marginBottom: 4 }}>
                                {savedShared ? (
                                  <>
                                    <strong>{em.route_count || 0}</strong> sender{(em.route_count || 0) === 1 ? '' : 's'}
                                    {' · '}
                                    {em.route_client_count || 0} client{(em.route_client_count || 0) === 1 ? '' : 's'}
                                  </>
                                ) : (
                                  <span style={{ color: '#B45309' }}>Save to start mapping senders</span>
                                )}
                              </div>
                              <button
                                type="button"
                                style={{ ...linkAction, opacity: savedShared ? 1 : 0.5 }}
                                disabled={!savedShared}
                                onClick={() => openEmailRoutes(em.email_id)}
                              >
                                Manage mapping →
                              </button>
                            </div>
                          ) : clientPack.loading ? (
                            <span style={{ color: '#9CA3AF' }}>Loading clients…</span>
                          ) : clients.length === 0 ? (
                            <span style={{ color: '#9CA3AF' }}>No clients on product</span>
                          ) : (
                            <div style={{ minWidth: 200, maxWidth: 300 }} className="pm-client-ss">
                              <SearchableSelect
                                options={clientSelectOptions(clients, {
                                  legacyValue: draft.client_name || '',
                                })}
                                value={draft.client_name || ''}
                                onChange={(v) => setEmailDraftLink(em.email_id, { client_name: v || '' })}
                                placeholder="— Select client —"
                                searchPlaceholder="Search clients…"
                                aria-label="Client"
                              />
                            </div>
                          )}
                        </td>
                        {canMapEmail && (
                          <td style={td}>
                            <button
                              type="button"
                              className="tk-btn tk-btn-primary"
                              disabled={!dirty && complete}
                              onClick={() => saveEmailLink(em.email_id)}
                              style={{ padding: '6px 12px', fontSize: '12px' }}
                            >
                              {draft.product_id ? 'Save' : 'Unlink'}
                            </button>
                          </td>
                        )}
                        {isPlatformAdmin && (
                          <td style={td}>
                            <button type="button" onClick={() => removeEmail(em)} style={linkDanger}>Remove</button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
        </div>
          </div>
    );
  };

  const unmappedTotal = groups.filter((g) => !g.product_id).length
    + emails.filter((em) => !em.product_id).length;
  const PAGE_VIEWS = [
    { id: 'products', label: 'Products', icon: Package },
    ...(isPlatformAdmin ? [{ id: 'unmapped', label: 'Unmapped groups & mailboxes', icon: Tray }] : []),
  ];

  const SETTINGS_TABS = tabsFor(selected);
  const myAccess = selected?.my_access || null;
  const canManageAccess = !!myAccess && (myAccess.is_admin || myAccess.is_manager);
  // Members acting through the Access grant cannot edit themselves or the manager.
  const lockedForMe = !!permUser && !canManageAccess
    && (permUser.user_id === user?.user_id || permUser.org_role === 'manager');
  const accessHeading = {
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: '#1D4ED8',
    margin: '4px 0 10px',
  };

  return (
    <ModuleShell
      className="pm-module"
      title="Product Management"
      subtitle={
        isPlatformAdmin
          ? 'Pick a product to manage its settings, data, clients, mappings, team and access.'
          : 'Pick a product — you see the sections you manage or were given access to.'
      }
      icon={Package}
      headerActions={
        pageView === 'products' && isPlatformAdmin ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={() => setShowCompaniesPanel(true)}
              className="tk-btn tk-btn-secondary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              Companies
            </button>
        <button
          type="button"
          onClick={() => { setShowCreate(true); setSelected(null); setError(null); }}
              className="tk-btn tk-btn-primary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
              <Plus size={16} weight="bold" />
          New product
        </button>
      </div>
        ) : null
      }
      subnav={(
        <nav className="tk-subnav" aria-label="Product management sections" role="tablist">
          {PAGE_VIEWS.map((v) => {
            const on = pageView === v.id;
            const Icon = v.icon;
            return (
              <button
                key={v.id}
                type="button"
                role="tab"
                aria-selected={on}
                className={`tk-subnav-link${on ? ' is-active' : ''}`}
                onClick={() => {
                  setPageView(v.id);
                  setShowCreate(false);
                  setError(null);
                }}
              >
                <Icon size={15} />
                <span>{v.label}</span>
              {v.id === 'unmapped' && unmappedTotal > 0 && (
                <span style={{
                  background: '#FEF3C7',
                  color: '#B45309',
                  fontSize: '10px',
                  fontWeight: 700,
                  padding: '1px 6px',
                  borderRadius: '999px',
                }}
                >
                  {unmappedTotal}
                </span>
              )}
              </button>
            );
          })}
        </nav>
      )}
    >
    <div className="pm-page">

      {pageView === 'unmapped' && isPlatformAdmin && (
        <>
          <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 4px' }}>
            WhatsApp groups and mailboxes not mapped to any product yet. Once mapped, each one moves
            into that product&apos;s WhatsApp / Email mapping tab.
          </p>
          {canMapWhatsApp && !waProductIds && renderWhatsApp(groups.filter((g) => !g.product_id), { scoped: false })}
          {renderEmail(emails.filter((em) => !em.product_id), { scoped: false })}
        </>
      )}

      {pageView === 'products' && (
      <>
      {showCreate && isPlatformAdmin && (
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <h2 style={{ fontSize: '12px', fontWeight: '600', margin: 0 }}>Create product</h2>
            <button type="button" onClick={() => setShowCreate(false)} style={iconBtn} aria-label="Close">
              <X size={20} />
            </button>
          </div>
          <form onSubmit={createProduct} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 20px' }}>
            <Field label="Product name" required hint="Shown in App Support ticket dropdown">
              <input
                required
                value={form.name}
                onChange={(e) => onNameChange(e.target.value)}
                placeholder="e.g. HRMS Portal"
                style={inputStyle}
              />
            </Field>
            <Field label="Product key" required hint="Unique identifier (auto-generated from name)">
              <input
                required
                value={form.key}
                onChange={(e) => { setKeyTouched(true); setForm({ ...form, key: e.target.value }); }}
                placeholder="e.g. hrms_portal"
                style={inputStyle}
              />
            </Field>
            <Field label="Company" hint="Optional — which company this product belongs to">
              <select
                value={form.company_id}
                onChange={(e) => setForm({ ...form, company_id: e.target.value })}
                style={inputStyle}
              >
                <option value="">—</option>
                {companies.map((c) => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
              </select>
            </Field>
            <div style={{ gridColumn: '1 / -1' }}>
              <Field label="Description" hint="Optional — internal notes about this product">
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  placeholder="Brief description for admins…"
                  style={{ ...inputStyle, resize: 'vertical' }}
                />
              </Field>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <Field label="Handles ticket domains" hint="Tickets in these domains route to this product's team.">
                <DomainCheckboxes
                  value={form.handles_domains}
                  onChange={(next) => setForm({ ...form, handles_domains: next })}
                />
              </Field>
            </div>
            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '10px', justifyContent: 'flex-end', paddingTop: '8px' }}>
              <button type="button" onClick={() => setShowCreate(false)} style={secondaryBtn}>Cancel</button>
              <button type="submit" disabled={submitting} style={{ ...primaryBtn, opacity: submitting ? 0.7 : 1 }}>
                {submitting ? 'Creating…' : 'Create product'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Horizontal product strip + settings (page scrolls via .content-area) */}
      <div className="pm-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '10px', flexWrap: 'wrap' }}>
          <h2 style={{ fontSize: '14px', fontWeight: 600, margin: 0, color: '#111827' }}>
            Products ({products.length})
          </h2>
          <ListSearch
            value={productSearch}
            onChange={setProductSearch}
            placeholder="Search products…"
            resultCount={filteredProducts.length}
            totalCount={products.length}
            maxWidth="280px"
            style={{ marginBottom: 0 }}
          />
        </div>
          {products.length === 0 ? (
          <p style={{ color: '#9CA3AF', fontSize: '12px', margin: 0 }}>
            {isPlatformAdmin
              ? 'No products yet. Create one to enable App Support tickets.'
              : 'No products yet. Ask the product manager to give you access on the product\'s Access tab.'}
          </p>
        ) : filteredProducts.length === 0 ? (
          <p style={{ color: '#9CA3AF', fontSize: '12px', margin: 0 }}>No products match “{productSearch}”.</p>
        ) : (
          <div className="pm-product-strip">
              {filteredProducts.map((p) => {
                const active = selected?.product_id === p.product_id;
                return (
                    <button
                  key={p.product_id}
                      type="button"
                      onClick={() => openProduct(p.product_id)}
                      style={{
                    flex: '0 0 auto',
                    minWidth: '168px',
                    maxWidth: '220px',
                        textAlign: 'left',
                    padding: '12px 14px',
                        border: active ? '2px solid #002FA7' : '1px solid #E5E7EB',
                    borderRadius: '12px',
                    background: active ? '#EFF6FF' : '#FFFFFF',
                        cursor: 'pointer',
                      }}
                    >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Package size={18} color={active ? '#002FA7' : '#6B7280'} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{
                        fontWeight: 600,
                        fontSize: '12px',
                        color: '#111827',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                      >
                        {p.name}
                      </div>
                      <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '2px' }}>{p.key}</div>
                        </div>
                      </div>
                    </button>
                );
              })}
          </div>
          )}
        </div>

      {/* Selected product settings */}
          {selected ? (
        <div className="pm-card">
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '12px',
            marginBottom: '10px',
            paddingBottom: '12px',
            borderBottom: '1px solid #E5E7EB',
          }}
          >
            <div>
              <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', color: '#1D4ED8', marginBottom: '4px' }}>
                PRODUCT MANAGER
              </div>
              {selected.manager ? (
                <>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: '#111827' }}>{selected.manager.name}</div>
                  <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>{selected.manager.email}</div>
                </>
              ) : (
                <div style={{ fontSize: '13px', color: '#9CA3AF' }}>No manager assigned yet — add one in the Team tab.</div>
              )}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '10px' }}>
                <Badge>{selected.name}</Badge>
                <Badge>Key: {selected.key}</Badge>
                <Badge>{selected.active ? 'Active' : 'Inactive'}</Badge>
              </div>
            </div>
            {isPlatformAdmin && (
              <button type="button" onClick={deleteProduct} style={dangerBtn} title="Delete product">
                <Trash size={16} weight="bold" />
                Delete
              </button>
            )}
          </div>

          <div
            role="tablist"
            aria-label="Product settings"
            style={{
              display: 'flex',
              gap: '0',
              overflowX: 'auto',
              borderBottom: '1px solid #E5E7EB',
              marginBottom: '12px',
            }}
          >
            {SETTINGS_TABS.map((tab) => {
              const on = settingsTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  onClick={() => setSettingsTab(tab.id)}
                  style={{
                    flex: '0 0 auto',
                    padding: '10px 14px',
                    border: 'none',
                    borderBottom: on ? '2px solid #002FA7' : '2px solid transparent',
                    background: 'transparent',
                    color: on ? '#002FA7' : '#6B7280',
                    fontWeight: on ? 600 : 500,
                    fontSize: '12px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          <div>
          {settingsTab === 'general' && (
            <div style={{ display: 'grid', gap: '12px', maxWidth: '560px' }}>
                    <Field label="Product name" required>
                      <input
                        value={editMeta.name}
                        onChange={(e) => setEditMeta({ ...editMeta, name: e.target.value })}
                        style={inputStyle}
                      />
                    </Field>
                    <Field label="Description">
                      <textarea
                        value={editMeta.description}
                        onChange={(e) => setEditMeta({ ...editMeta, description: e.target.value })}
                  rows={4}
                        style={{ ...inputStyle, resize: 'vertical' }}
                        placeholder="What this product is for…"
                      />
                    </Field>
              <Field label="Company" hint="Optional — which company this product belongs to">
                <select
                  value={editMeta.company_id}
                  onChange={(e) => setEditMeta({ ...editMeta, company_id: e.target.value })}
                  style={inputStyle}
                >
                  <option value="">—</option>
                  {companies.map((c) => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
                </select>
                    </Field>
                    <button
                      type="button"
                      onClick={saveProductMeta}
                      disabled={savingMeta}
                style={{ ...primaryBtn, alignSelf: 'flex-start', opacity: savingMeta ? 0.7 : 1 }}
                    >
                {savingMeta ? 'Saving…' : 'Save'}
                    </button>
                  </div>
          )}

          {settingsTab === 'routing' && (
            <div>
                <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 12px' }}>
                Tickets in the selected domains are routed to this product&apos;s team.
                </p>
                <DomainCheckboxes value={domainEdit} onChange={setDomainEdit} />
                <button
                  type="button"
                  onClick={saveDomains}
                  disabled={savingDomains}
                style={{ ...primaryBtn, marginTop: '14px', opacity: savingDomains ? 0.7 : 1 }}
                >
                  {savingDomains ? 'Saving…' : 'Save routing'}
                </button>
              </div>
          )}

          {settingsTab === 'assignment' && (
            <div>
              <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 12px' }}>
                Controls what happens when an agent, client, or WhatsApp raises a ticket for this product.
              </p>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '16px',
                  padding: '12px 14px',
                  border: '1px solid #E5E7EB',
                  borderRadius: '10px',
                  marginBottom: '16px',
                  background: acceptTickets ? '#F8FAFC' : '#F9FAFB',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#111827' }}>
                    Accept new tickets
                </div>
                  <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>
                    {acceptTickets
                      ? 'On — users can select this product when raising a ticket.'
                      : 'Off — this product is grayed out and cannot be selected when raising a ticket.'}
                  </div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={acceptTickets}
                  aria-label="Accept new tickets"
                  disabled={savingAcceptTickets}
                  onClick={() => saveAcceptTickets(!acceptTickets)}
                  style={{
                    flexShrink: 0,
                    width: '44px',
                    height: '26px',
                    borderRadius: '999px',
                    border: 'none',
                    padding: '2px',
                    cursor: savingAcceptTickets ? 'wait' : 'pointer',
                    background: acceptTickets ? '#002FA7' : '#D1D5DB',
                    transition: 'background 0.15s ease',
                    opacity: savingAcceptTickets ? 0.7 : 1,
                  }}
                >
                  <span
                    style={{
                      display: 'block',
                      width: '22px',
                      height: '22px',
                      borderRadius: '50%',
                      background: '#fff',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                      transform: acceptTickets ? 'translateX(18px)' : 'translateX(0)',
                      transition: 'transform 0.15s ease',
                    }}
                  />
                </button>
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '16px',
                  padding: '12px 14px',
                  border: '1px solid #E5E7EB',
                  borderRadius: '10px',
                  marginBottom: '16px',
                  background: whatsappManualUpdates ? '#F0FDF4' : '#F9FAFB',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#111827' }}>
                    WhatsApp updates for manual tickets
              </div>
                  <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>
                    {whatsappManualUpdates
                      ? 'On — when staff create a ticket and the company/client is mapped to a WhatsApp group, create / assign / pending / completed updates post to that group (not on closed). Works even if bridge intake is stopped for the group.'
                      : 'Off — manually created tickets do not post updates to WhatsApp groups.'}
                  </div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={whatsappManualUpdates}
                  aria-label="WhatsApp updates for manual tickets"
                  disabled={savingWaManual}
                  onClick={() => saveWhatsappManualUpdates(!whatsappManualUpdates)}
                  style={{
                    flexShrink: 0,
                    width: '44px',
                    height: '26px',
                    borderRadius: '999px',
                    border: 'none',
                    padding: '2px',
                    cursor: savingWaManual ? 'wait' : 'pointer',
                    background: whatsappManualUpdates ? '#15803D' : '#D1D5DB',
                    transition: 'background 0.15s ease',
                    opacity: savingWaManual ? 0.7 : 1,
                  }}
                >
                  <span
                    style={{
                      display: 'block',
                      width: '22px',
                      height: '22px',
                      borderRadius: '50%',
                      background: '#fff',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                      transform: whatsappManualUpdates ? 'translateX(18px)' : 'translateX(0)',
                      transition: 'transform 0.15s ease',
                    }}
                  />
                </button>
              </div>

              <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 12px' }}>
                Click <strong>Save assignment setting</strong> after changing the checkbox below.
              </p>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '13px' }}>
                <input
                  type="checkbox"
                  checked={assignToManager}
                  onChange={(e) => setAssignToManager(e.target.checked)}
                  style={{ marginTop: '3px' }}
                />
                <span>
                  <strong>Route new tickets to product manager queue</strong>
                  <br />
                  <span style={{ color: '#6B7280', fontSize: '12px' }}>
                    {assignToManager
                      ? 'On — new tickets appear in the manager queue (still unassigned until someone starts work or assigns).'
                      : 'Off — new tickets stay fully unassigned for any team member to pick up (in progress = claim + My Tasks).'}
                  </span>
                </span>
              </label>
              <button
                type="button"
                onClick={saveAssignConfig}
                disabled={savingAssignConfig}
                style={{ ...primaryBtn, marginTop: '14px', opacity: savingAssignConfig ? 0.7 : 1 }}
              >
                {savingAssignConfig ? 'Saving…' : 'Save assignment setting'}
              </button>

              <div style={{
                marginTop: '20px',
                paddingTop: '18px',
                borderTop: '1px solid #E5E7EB',
              }}
              >
                <h4 style={{ fontSize: '13px', fontWeight: 600, margin: '0 0 6px', color: '#111827' }}>
                  Closure reminders (completed → close)
                </h4>
                <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 14px', lineHeight: 1.45 }}>
                  When tickets stay in <strong>Completed</strong> without being closed, send one digest email
                  listing all of them. Assignees always receive it when reminders are on; product manager is optional.
                </p>

                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '13px', marginBottom: '12px' }}>
                  <input
                    type="checkbox"
                    checked={closureReminderEnabled}
                    onChange={(e) => setClosureReminderEnabled(e.target.checked)}
                    style={{ marginTop: '3px' }}
                  />
                  <span>
                    <strong>Enable closure reminders</strong>
                    <br />
                    <span style={{ color: '#6B7280', fontSize: '12px' }}>
                      {closureReminderEnabled
                        ? 'On — digests go to assignees on the interval below.'
                        : 'Off — no reminder emails for this product.'}
                    </span>
                  </span>
                </label>

                <label style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  cursor: closureReminderEnabled ? 'pointer' : 'not-allowed',
                  fontSize: '13px',
                  marginBottom: '12px',
                  opacity: closureReminderEnabled ? 1 : 0.55,
                }}
                >
                  <input
                    type="checkbox"
                    checked={closureReminderNotifyManager}
                    disabled={!closureReminderEnabled}
                    onChange={(e) => setClosureReminderNotifyManager(e.target.checked)}
                    style={{ marginTop: '3px' }}
                  />
                  <span>
                    <strong>Also email product manager</strong>
                    <br />
                    <span style={{ color: '#6B7280', fontSize: '12px' }}>
                      Assignees are always included. Uncheck to notify assignees only.
                    </span>
                  </span>
                </label>

                <div style={{ marginBottom: '12px', opacity: closureReminderEnabled ? 1 : 0.55 }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px' }}>
                    Reminder interval (minutes)
                  </label>
                  <input
                    type="number"
                    min={15}
                    step={15}
                    disabled={!closureReminderEnabled}
                    value={closureReminderInterval}
                    onChange={(e) => setClosureReminderInterval(e.target.value)}
                    className="input-field"
                    style={{ maxWidth: '140px' }}
                  />
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: '6px 0 0' }}>
                    Minimum 15. First reminder after a ticket has been completed this long; then repeats on this interval.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={saveClosureReminderConfig}
                  disabled={savingClosureReminder}
                  style={{ ...primaryBtn, opacity: savingClosureReminder ? 0.7 : 1 }}
                >
                  {savingClosureReminder ? 'Saving…' : 'Save closure reminder settings'}
                </button>
              </div>
            </div>
          )}

          {settingsTab === 'locations' && selected && (
            <ProductLocationsPanel
              productId={selected.product_id}
              productName={selected.name}
            />
          )}

          {settingsTab === 'team' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <UsersThree size={20} color="#374151" />
                  <h3 style={{ fontSize: '14px', fontWeight: 600, margin: 0 }}>Team members</h3>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setMemberForm({ user: null, org_role: 'member', department_id: '', client_ids: [] });
                    setMemberClientsOpen(false);
                    setShowAddMember(true);
                    setError(null);
                  }}
                  style={primaryBtn}
                >
                  <Plus size={16} weight="bold" />
                  Add team member
                </button>
              </div>

              <div style={{
                marginBottom: '16px',
                padding: '12px 14px',
                border: '1px solid #E5E7EB',
                borderRadius: '10px',
                background: '#F9FAFB',
              }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', marginBottom: '10px' }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#111827' }}>Departments</div>
                    <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>
                      Group staff (e.g. Development, Support). Create as needed — not linked to user HR department.
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
                  {productDepartments.length === 0 && (
                    <span style={{ fontSize: '12px', color: '#9CA3AF' }}>No departments yet</span>
                  )}
                  {productDepartments.map((d) => (
                    <div
                      key={d.id}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '4px 8px',
                        borderRadius: '8px',
                        background: 'white',
                        border: '1px solid #E5E7EB',
                        fontSize: '12px',
                      }}
                    >
                      {renamingDeptId === d.id ? (
                        <>
                          <input
                            value={renameDeptName}
                            onChange={(e) => setRenameDeptName(e.target.value)}
                            style={{ ...inputStyle, padding: '4px 8px', minWidth: '120px' }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveRenameDepartment();
                              if (e.key === 'Escape') {
                                setRenamingDeptId(null);
                                setRenameDeptName('');
                              }
                            }}
                            autoFocus
                          />
                          <button
                            type="button"
                            onClick={saveRenameDepartment}
                            disabled={savingDept || !renameDeptName.trim()}
                            style={{ ...linkBtn, opacity: (savingDept || !renameDeptName.trim()) ? 0.5 : 1 }}
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={() => { setRenamingDeptId(null); setRenameDeptName(''); }}
                            style={linkMuted}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <span style={{ fontWeight: 600, color: '#374151' }}>{d.name}</span>
                          <button
                            type="button"
                            onClick={() => {
                              setRenamingDeptId(d.id);
                              setRenameDeptName(d.name);
                            }}
                            style={linkMuted}
                          >
                            Rename
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteDepartment(d)}
                            disabled={savingDept}
                            style={linkDanger}
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    value={newDeptName}
                    onChange={(e) => setNewDeptName(e.target.value)}
                    placeholder="New department name…"
                    style={{ ...inputStyle, maxWidth: '240px', padding: '6px 10px' }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') createDepartment();
                    }}
                  />
                  <button
                    type="button"
                    onClick={createDepartment}
                    disabled={savingDept || !newDeptName.trim()}
                    style={{ ...secondaryBtn, opacity: (savingDept || !newDeptName.trim()) ? 0.6 : 1 }}
                  >
                    <Plus size={14} weight="bold" />
                    Add department
                  </button>
                </div>
              </div>

              <ListSearch
                value={memberSearch}
                onChange={setMemberSearch}
                placeholder="Search team members…"
                resultCount={filteredTeamMembers.length}
                totalCount={staffMembers.length}
                maxWidth="100%"
              />

              {filteredTeamMembers.length === 0 ? (
                <div className="pm-team-table-wrap">
                  <table>
                    <tbody>
                      <tr>
                        <td style={{ ...td, color: '#9CA3AF' }}>
                          {memberSearch ? `No members match "${memberSearch}"` : 'No team members yet'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              ) : (
                staffByDepartment.map((group) => {
                  if (group.members.length === 0 && group.id === null && productDepartments.length > 0) {
                    return (
                      <div key="unassigned" style={{ marginTop: '16px' }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'baseline',
                          gap: '8px',
                          marginBottom: '8px',
                        }}
                        >
                          <h4 style={{ fontSize: '13px', fontWeight: 600, margin: 0, color: '#6B7280' }}>
                            Unassigned
                          </h4>
                          <span style={{ fontSize: '12px', color: '#9CA3AF' }}>0</span>
                        </div>
                        <div style={{ fontSize: '12px', color: '#9CA3AF', padding: '8px 0' }}>
                          No unassigned members
                        </div>
                      </div>
                    );
                  }
                  if (group.members.length === 0) return null;
                  return (
                    <div key={group.id || 'unassigned'} style={{ marginTop: '16px' }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: '8px',
                        marginBottom: '8px',
                      }}
                      >
                        <h4 style={{
                          fontSize: '13px',
                          fontWeight: 600,
                          margin: 0,
                          color: group.id ? '#111827' : '#6B7280',
                        }}
                        >
                          {group.name}
                        </h4>
                        <span style={{ fontSize: '12px', color: '#9CA3AF' }}>
                          {group.members.length}
                        </span>
                      </div>
                      <div className="pm-team-table-wrap">
                        <table>
                  <thead>
                            <tr style={{ textAlign: 'left' }}>
                      <th style={th}>Name</th>
                      <th style={th}>Team role</th>
                      <th style={th}>Designation</th>
                              <th style={th}>Department</th>
                              <th style={th}>Client</th>
                      <th style={th} />
                    </tr>
                  </thead>
                  <tbody>
                            {group.members.map((m) => (
                      <tr key={m.user_id} style={{ borderBottom: '1px solid #E5E7EB' }}>
                        <td style={td}>
                                  <div style={{ fontWeight: 500 }}>{m.name}</div>
                          <div style={{ fontSize: '12px', color: '#6B7280' }}>{m.email}</div>
                          {m.other_products?.length > 0 && (
                            <div style={{ fontSize: '11px', color: '#B45309', marginTop: '4px' }}>
                              Also on: {m.other_products.join(', ')}
                            </div>
                          )}
                        </td>
                        <td style={td}><span style={rolePill}>{m.org_role}</span></td>
                        <td style={td}>{m.designation || '—'}</td>
                        <td style={td}>
                                  <select
                                    value={m.department_id || ''}
                                    disabled={movingMemberId === m.user_id}
                                    onChange={(e) => moveMemberDepartment(m.user_id, e.target.value)}
                                    style={{ ...inputStyle, padding: '4px 8px', minWidth: '140px' }}
                                    aria-label={`Move ${m.name} to department`}
                                  >
                                    <option value="">Unassigned</option>
                                    {productDepartments.map((d) => (
                                      <option key={d.id} value={d.id}>{d.name}</option>
                                    ))}
                                  </select>
                                </td>
                                <td style={td}>
                                  {editingClientMemberId === m.user_id ? (
                                    <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '6px', padding: '8px', minWidth: '220px', maxWidth: '280px' }}>
                                      <ClientPickList
                                        clients={productClients}
                                        selectedIds={editingClientIds}
                                        onToggle={toggleEditingClientIdPM}
                                        maxHeight={140}
                                      />
                                      <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                                        <button type="button" onClick={() => saveMemberClientsPM(m.user_id)}
                                          disabled={movingMemberId === m.user_id}
                                          style={{ ...linkAction, fontSize: '11px' }}>
                                          {movingMemberId === m.user_id ? 'Saving…' : 'Save'}
                            </button>
                                        <button type="button" onClick={() => setEditingClientMemberId(null)}
                                          disabled={movingMemberId === m.user_id}
                                          style={{ ...linkMuted, fontSize: '11px' }}>
                                          Cancel
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <button type="button" onClick={() => openClientEditorPM(m)} style={{ ...linkBtn, fontSize: '12px', fontWeight: 400 }}>
                                      {(m.client_ids || []).length
                                        ? m.client_ids.map((cid) => productClients.find((c) => c.client_id === cid)?.name || cid).join(', ')
                                        : 'No client'}
                                    </button>
                                  )}
                                </td>
                                <td style={td}>
                            <button type="button" onClick={() => removeMember(m.user_id)} style={linkDanger}>Remove</button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                          </div>
                    </div>
                  );
                })
              )}

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '12px',
                margin: '28px 0 12px',
                paddingTop: '20px',
                borderTop: '1px solid #E5E7EB',
              }}
              >
                <div>
                  <h3 style={{ fontSize: '14px', fontWeight: 600, margin: '0 0 4px' }}>Ticket raisers</h3>
                  <p style={{ fontSize: '12px', color: '#6B7280', margin: 0 }}>
                    Agents and clients who may raise App Support tickets for this product.
                    Same assignments appear on the Users page.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => { setShowAddRaiser(true); setError(null); }}
                  style={primaryBtn}
                >
                  <Plus size={16} weight="bold" />
                  Add raiser
                </button>
              </div>

              <ListSearch
                value={raiserSearch}
                onChange={setRaiserSearch}
                placeholder="Search ticket raisers…"
                resultCount={filteredRaisers.length}
                totalCount={raiserMembers.length}
                maxWidth="100%"
              />

              <div className="pm-team-table-wrap">
                <table>
                  <thead>
                    <tr style={{ textAlign: 'left' }}>
                      <th style={th}>Name</th>
                      <th style={th}>Platform role</th>
                      <th style={th} />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRaisers.length ? filteredRaisers.map((m) => (
                      <tr key={m.user_id} style={{ borderBottom: '1px solid #E5E7EB' }}>
                        <td style={td}>
                          <div style={{ fontWeight: 500 }}>{m.name}</div>
                          <div style={{ fontSize: '12px', color: '#6B7280' }}>{m.email}</div>
                          {m.other_products?.length > 0 && (
                            <div style={{ fontSize: '11px', color: '#B45309', marginTop: '4px' }}>
                              Also on: {m.other_products.join(', ')}
                            </div>
                          )}
                        </td>
                        <td style={td}><span style={rolePill}>raiser</span></td>
                        <td style={td}>
                          <button
                            type="button"
                            onClick={() => removeMember(m.user_id, 'raiser')}
                            style={linkDanger}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={3} style={{ ...td, color: '#9CA3AF' }}>
                          {raiserSearch
                            ? `No raisers match "${raiserSearch}"`
                            : 'No agents or clients assigned yet'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {settingsTab === 'access' && (
                    <div>
              <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 12px' }}>
                Select a team member or ticket raiser to set what they can do on <strong>{selected.name}</strong> only.
                <strong> Product modules</strong> decide which tabs of this product a team member sees (managers always see all).
                <strong> Ticket actions</strong> start from their role (manager / lead / member / raiser) and can be extended here.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px', alignItems: 'start' }}>
                <div className="pm-access-list">
                  {staffMembers.length === 0 && raiserMembers.length === 0 ? (
                    <p style={{ padding: '14px', color: '#9CA3AF', fontSize: '12px', margin: 0 }}>
                      Add team members or ticket raisers first.
                    </p>
                  ) : (
                    [
                      { heading: 'Team', members: staffMembers },
                      { heading: 'Ticket raisers', members: raiserMembers },
                    ].map((group) => (
                      group.members.length === 0 ? null : (
                        <div key={group.heading}>
                          <div style={{
                            padding: '8px 12px 4px',
                            fontSize: '11px',
                            fontWeight: 700,
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                            color: '#6B7280',
                            background: '#F9FAFB',
                            borderBottom: '1px solid #F3F4F6',
                          }}
                          >
                            {group.heading}
                          </div>
                          {group.members.map((m) => {
                            const on = permUser?.user_id === m.user_id;
                            return (
                              <button
                                key={m.user_id}
                                type="button"
                                onClick={() => openTicketPerms(m)}
                                style={{
                                  display: 'block',
                                  width: '100%',
                                  textAlign: 'left',
                                  padding: '10px 12px',
                                  border: 'none',
                                  borderBottom: '1px solid #F3F4F6',
                                  background: on ? '#EFF6FF' : 'white',
                                  cursor: 'pointer',
                                }}
                              >
                                <div style={{ fontWeight: 600, fontSize: '12px', color: '#111827' }}>{m.name}</div>
                                <div style={{ fontSize: '11px', color: '#6B7280' }}>{m.org_role} · {m.email}</div>
                              </button>
                            );
                          })}
                        </div>
                      )
                    ))
                  )}
                  </div>

                <div style={{ border: '1px solid #E5E7EB', borderRadius: '10px', padding: '14px', minHeight: '200px' }}>
                  {!permUser ? (
                    <p style={{ color: '#9CA3AF', fontSize: '12px', margin: 0 }}>Select a team member or ticket raiser to edit their access.</p>
                  ) : permLoading || !permState ? (
                    <p style={{ color: '#6B7280', fontSize: '12px', margin: 0 }}>Loading permissions…</p>
                  ) : (
                    <>
                      <div style={{ marginBottom: '12px' }}>
                        <h4 style={{ fontSize: '14px', fontWeight: 600, margin: '0 0 4px' }}>
                          {permUser.name}
                        </h4>
                        <p style={{ fontSize: '12px', color: '#6B7280', margin: 0 }}>
                          {permUser.org_role} · {permUser.email}
                        </p>
                        {lockedForMe && (
                          <p style={{ fontSize: '12px', color: '#B45309', margin: '6px 0 0' }}>
                            {permUser.user_id === user?.user_id
                              ? 'You cannot change your own access — ask the product manager.'
                              : 'Only an admin or the product manager can change the manager\'s access.'}
                          </p>
                        )}
                      </div>

                      <h4 style={accessHeading}>Product modules</h4>
                      {permUser.org_role === 'raiser' ? (
                        <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 16px' }}>
                          Ticket raisers only get ticket actions — product modules are for team members.
                        </p>
                      ) : permUser.org_role === 'manager' ? (
                        <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 16px' }}>
                          All modules — the product manager always has full access to {selected.name}.
                        </p>
                      ) : (
                        <>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px', marginBottom: '10px' }}>
                            {TEAM_ACCESS_OPTIONS.map((opt) => {
                              const on = editingAccess.includes(opt.id);
                              return (
                                <label
                                  key={opt.id}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '8px',
                                    padding: '8px',
                                    borderRadius: '8px',
                                    border: on ? '1px solid #93C5FD' : '1px solid #E5E7EB',
                                    background: on ? '#EFF6FF' : 'white',
                                    fontSize: '12px',
                                    cursor: lockedForMe ? 'not-allowed' : 'pointer',
                                    opacity: lockedForMe ? 0.6 : 1,
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={on}
                                    disabled={lockedForMe}
                                    onChange={() => setEditingAccess((cur) => (
                                      cur.includes(opt.id) ? cur.filter((x) => x !== opt.id) : [...cur, opt.id]
                                    ))}
                                    style={{ marginTop: '2px' }}
                                  />
                                  <span>
                                    <span style={{ fontWeight: 600, color: '#111827', display: 'block' }}>{opt.label}</span>
                                    <span style={{ fontSize: '11px', color: '#9CA3AF' }}>{opt.hint}</span>
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                          <button
                            type="button"
                            onClick={saveMemberAccess}
                            disabled={savingAccess || lockedForMe}
                            style={{ ...primaryBtn, marginBottom: '18px', opacity: (savingAccess || lockedForMe) ? 0.6 : 1 }}
                          >
                            {savingAccess ? 'Saving…' : 'Save modules'}
                          </button>
                        </>
                      )}

                      <h4 style={accessHeading}>Ticket actions</h4>
                      <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 10px' }}>
                        Role defaults for <strong>{permState.org_role || permUser.org_role}</strong>
                        {permState.is_customized ? ' · customized' : ' · using role defaults'}
                      </p>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px', marginBottom: '14px' }}>
                        {Object.keys(permState.labels || {}).map((key) => {
                          const checked = !!permState.permissions?.[key];
                          const isDefault = !!permState.defaults?.[key];
                          const isExtra = checked && !isDefault;
                          return (
                            <label
                              key={key}
                              style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '8px',
                                padding: '8px',
                                borderRadius: '8px',
                                border: isExtra ? '1px solid #93C5FD' : '1px solid #E5E7EB',
                                background: isExtra ? '#EFF6FF' : 'white',
                                fontSize: '12px',
                                cursor: 'pointer',
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={lockedForMe}
                                onChange={() => togglePerm(key)}
                                style={{ marginTop: '2px' }}
                              />
                              <span>
                                <span style={{ fontWeight: 600, color: '#111827', display: 'block' }}>
                                  {permState.labels[key]}
                                </span>
                                <span style={{ fontSize: '11px', color: '#9CA3AF' }}>
                                  {isDefault ? 'Role default' : isExtra ? 'Extra grant' : 'Not granted'}
                                </span>
                              </span>
                            </label>
                          );
                        })}
                      </div>
                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={saveTicketPerms}
                          disabled={permSaving || lockedForMe}
                          style={{ ...primaryBtn, opacity: (permSaving || lockedForMe) ? 0.6 : 1 }}
                        >
                          {permSaving ? 'Saving…' : 'Save ticket actions'}
                        </button>
                        <button
                          type="button"
                          onClick={resetTicketPerms}
                          disabled={permSaving || lockedForMe}
                          style={secondaryBtn}
                        >
                          Reset to role defaults
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
                </div>
              )}

          {settingsTab === 'clients_branches' && (
            <ClientsBranchesPanel
              key={selected.product_id}
              productId={selected.product_id}
              productName={selected.name}
            />
          )}

          {settingsTab === 'whatsapp' && renderWhatsApp(
            groups.filter((g) => g.product_id === selected.product_id),
            { scoped: true },
          )}

          {settingsTab === 'email' && renderEmail(
            emails.filter((em) => em.product_id === selected.product_id),
            { scoped: true },
          )}

          {settingsTab === 'widgets' && (
            <ClientWidgetsPanel
              key={selected.product_id}
              products={[selected]}
              lockedProductId={selected.product_id}
            />
          )}

          {settingsTab === 'team_rules' && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', maxWidth: '640px' }}>
              <Gear size={22} color="#002FA7" style={{ marginTop: '2px', flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>Team rules — {selected.name}</div>
                <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 12px' }}>
                  Applies when someone is added to this product&apos;s team. Other products keep their own rule.
                </p>
                {loadingTeamRules ? (
                  <p style={{ color: '#9CA3AF', fontSize: '12px', margin: 0 }}>Loading…</p>
                ) : (
                  <>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '12px', color: '#374151', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={productRules.allow}
                        onChange={(e) => setProductRules((r) => ({ ...r, allow: e.target.checked }))}
                        style={{ marginTop: '3px' }}
                      />
                      <span>
                        Allow people on this team to also hold roles (manager / lead / member) on other product teams
                      </span>
                    </label>
                    {productRules.inherited && (
                      <p style={{ fontSize: '11px', color: '#9CA3AF', margin: '8px 0 0' }}>
                        Not saved for this product yet — currently following the previous organisation-wide setting.
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={saveTeamRules}
                      disabled={savingTeamRules}
                      style={{ ...secondaryBtn, marginTop: '14px', opacity: savingTeamRules ? 0.7 : 1 }}
                    >
                      {savingTeamRules ? 'Saving…' : 'Save rules'}
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
          </div>
        </div>
      ) : (
        !showCreate && (
          <div className="pm-card" style={{ textAlign: 'center', padding: '40px 24px', color: '#9CA3AF' }}>
            <Package size={40} style={{ marginBottom: '8px', opacity: 0.4 }} />
            <p style={{ margin: 0, fontSize: '14px' }}>Select a product above to manage settings</p>
          </div>
        )
      )}
      </>
      )}

      {whitelistGroup && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Whitelist WhatsApp team members"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(17, 24, 39, 0.45)',
            zIndex: 80,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}
          onClick={closeWhitelistModal}
        >
          <div
            style={{
              ...cardStyle,
              width: '100%',
              maxWidth: '520px',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 40px rgba(0,0,0,0.18)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, gap: 12 }}>
              <div>
                <h3 style={{ fontSize: '15px', fontWeight: 700, margin: 0 }}>
                  Team / client list
                </h3>
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
                  {whitelistGroup.group_name || '(unnamed group)'}
                </div>
                <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>
                  Names appear from WhatsApp contacts and past messages — use search if a name is still missing.
                </div>
              </div>
              <button type="button" onClick={closeWhitelistModal} style={iconBtn} aria-label="Close">
                <X size={20} />
              </button>
            </div>

            <div role="radiogroup" aria-label="Who will you check in the list?" style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
                Step 1 — What should a check mean?
              </div>
              {[
                {
                  value: 'clients',
                  title: '✅ Checked = Tickets: Yes',
                  hint: 'Check the people whose messages should become tickets (clients).',
                },
                {
                  value: 'team',
                  title: '🚫 Checked = Tickets: No',
                  hint: 'Check the people whose messages should NOT become tickets (our staff).',
                },
              ].map((opt) => (
                <label
                  key={opt.value}
                  style={{
                    display: 'flex',
                    gap: 8,
                    alignItems: 'flex-start',
                    padding: '8px 10px',
                    border: `1px solid ${memberListMode === opt.value ? '#25D366' : '#E5E7EB'}`,
                    background: memberListMode === opt.value ? '#F0FDF4' : 'white',
                    borderRadius: 8,
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="radio"
                    name="member-list-mode"
                    value={opt.value}
                    checked={memberListMode === opt.value}
                    onChange={() => changeMemberListMode(opt.value)}
                    style={{ marginTop: 2 }}
                  />
                  <span>
                    <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#111827' }}>{opt.title}</span>
                    <span style={{ display: 'block', fontSize: 11, color: '#6B7280' }}>{opt.hint}</span>
                  </span>
                </label>
              ))}
              {memberListMode !== savedMemberListMode && (
                <div style={{ fontSize: 11, color: '#B45309' }}>
                  You changed the option, so all checks were cleared. Check the right people again, then save.
                </div>
              )}
            </div>

            <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
              {memberListMode === 'clients'
                ? 'Step 2 — Check the people who should get Tickets: Yes. The badge on each row shows the result.'
                : 'Step 2 — Check the people who should get Tickets: No. The badge on each row shows the result.'}
            </div>

            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                Search by mobile number
              </label>
              <div style={{ position: 'relative' }}>
                <MagnifyingGlass
                  size={16}
                  weight="bold"
                  style={{
                    position: 'absolute',
                    left: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#9CA3AF',
                    pointerEvents: 'none',
                  }}
                />
                <input
                  type="search"
                  inputMode="tel"
                  autoFocus
                  value={whitelistSearch}
                  onChange={(e) => setWhitelistSearch(e.target.value)}
                  placeholder="e.g. 9876543210 or +91…"
                  style={{ ...inputStyle, width: '100%', paddingLeft: 34 }}
                  aria-label="Search members by mobile number"
                />
              </div>
            </div>

            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: 8, marginBottom: 12 }}>
              {loadingWhitelist ? (
                <p style={{ padding: 16, margin: 0, color: '#6B7280', fontSize: 12 }}>Fetching members from WhatsApp…</p>
              ) : liveMembers.length === 0 ? (
                <p style={{ padding: 16, margin: 0, color: '#9CA3AF', fontSize: 12 }}>
                  No members returned. Ensure the bridge is connected and the linked number is in this group.
                </p>
              ) : (
                (() => {
                  const qRaw = whitelistSearch.trim().toLowerCase();
                  const qDigits = qRaw.replace(/\D/g, '');
                  const filtered = liveMembers.filter((m) => {
                    if (!qRaw) return true;
                    const phoneDigits = `${m.phone || ''}${m.display_phone || ''}${m.match_key || ''}`.replace(/\D/g, '');
                    const nameHay = (m.name || '').toLowerCase();
                    if (qDigits && phoneDigits.includes(qDigits)) return true;
                    if (qDigits.length >= 7 && phoneDigits.slice(-10).includes(qDigits.slice(-10))) return true;
                    if (nameHay.includes(qRaw)) return true;
                    return false;
                  });
                  if (filtered.length === 0) {
                    return (
                      <p style={{ padding: 16, margin: 0, color: '#9CA3AF', fontSize: 12 }}>
                        No members match “{whitelistSearch.trim()}”. Try the last 10 digits of the mobile number.
                      </p>
                    );
                  }
                  return (
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                      {filtered.map((m) => {
                        const checked = whitelistSelected.has(m.match_key);
                        const raisesTickets = memberListMode === 'clients' ? checked : !checked;
                        const label = m.name || 'Name not synced yet';
                        const phoneLabel = m.display_phone || (m.phone ? `+${String(m.phone).replace(/^\+/, '')}` : m.match_key);
                        return (
                          <li
                            key={m.match_key}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 10,
                              padding: '10px 12px',
                              borderBottom: '1px solid #F3F4F6',
                              background: checked ? '#F0FDF4' : 'white',
                              cursor: 'pointer',
                            }}
                            onClick={() => toggleWhitelistMember(m.match_key)}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleWhitelistMember(m.match_key)}
                              onClick={(e) => e.stopPropagation()}
                              aria-label={`${memberListMode === 'clients' ? 'Client' : 'Team member'} ${label} ${phoneLabel}`}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: m.name ? '#111827' : '#9CA3AF' }}>
                                {label}
                                {m.admin ? (
                                  <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, color: '#0369A1', background: '#E0F2FE', padding: '1px 6px', borderRadius: 999 }}>
                                    {m.admin}
                                  </span>
                                ) : null}
                              </div>
                              <div style={{ fontSize: 12, color: '#374151', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                                {phoneLabel}
                              </div>
                            </div>
                            <span
                              style={{
                                flexShrink: 0,
                                fontSize: 11,
                                fontWeight: 700,
                                padding: '3px 8px',
                                borderRadius: 999,
                                color: raisesTickets ? '#166534' : '#6B7280',
                                background: raisesTickets ? '#DCFCE7' : '#F3F4F6',
                              }}
                            >
                              {raisesTickets ? '✅ Tickets: Yes' : '🚫 Tickets: No'}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  );
                })()
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <div style={{ fontSize: 12, color: '#6B7280' }}>
                {(() => {
                  const yes = memberListMode === 'clients'
                    ? liveMembers.filter((m) => whitelistSelected.has(m.match_key)).length
                    : liveMembers.filter((m) => !whitelistSelected.has(m.match_key)).length;
                  return `✅ ${yes} Tickets: Yes · 🚫 ${liveMembers.length - yes} Tickets: No`;
                })()}
                {whitelistSearch.trim()
                  ? ` · filtering by “${whitelistSearch.trim()}”`
                  : ` · ${liveMembers.length} member(s)`}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" onClick={closeWhitelistModal} style={secondaryBtn} disabled={savingWhitelist}>
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveGroupWhitelist}
                  disabled={loadingWhitelist || savingWhitelist || (memberListMode === 'clients' && whitelistSelected.size === 0)}
                  title={memberListMode === 'clients' && whitelistSelected.size === 0 ? 'Check at least one client' : undefined}
                  style={{
                    ...primaryBtn,
                    opacity: (loadingWhitelist || savingWhitelist || (memberListMode === 'clients' && whitelistSelected.size === 0)) ? 0.7 : 1,
                  }}
                >
                  {savingWhitelist ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAddEmail && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Add email address"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(17, 24, 39, 0.45)',
            zIndex: 80,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}
          onClick={() => setShowAddEmail(false)}
        >
          <div
            style={{
              ...cardStyle,
              width: '100%',
              maxWidth: '480px',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 40px rgba(0,0,0,0.18)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ fontSize: '15px', fontWeight: 700, margin: 0 }}>
                {editingEmail ? `Edit mailbox "${editingEmail.email_address}"` : 'Add mailbox'}
              </h3>
              <button type="button" onClick={() => setShowAddEmail(false)} style={iconBtn} aria-label="Close">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={saveEmailForm}>
              <div style={{ marginBottom: '12px' }}>
                <Field label="Email address" required>
                  <input
                    type="email"
                    required
                    value={newEmailForm.email_address}
                    onChange={(e) => setNewEmailForm({ ...newEmailForm, email_address: e.target.value })}
                    placeholder="support.hyd@kapilchits.com"
                    style={inputStyle}
                  />
                </Field>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <Field label="Label" hint="Optional — helps tell branches/addresses apart">
                  <input
                    type="text"
                    value={newEmailForm.label}
                    onChange={(e) => setNewEmailForm({ ...newEmailForm, label: e.target.value })}
                    placeholder="e.g. Hyderabad"
                    style={inputStyle}
                  />
                </Field>
              </div>

              <p style={{ fontSize: '12px', fontWeight: 600, color: '#374151', margin: '16px 0 8px', paddingTop: '12px', borderTop: '1px solid #E5E7EB' }}>
                IMAP login for this mailbox
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <Field label="Host" required>
                  <input
                    type="text"
                    required
                    value={newEmailForm.imap_host}
                    onChange={(e) => setNewEmailForm({ ...newEmailForm, imap_host: e.target.value })}
                    placeholder="imap.gmail.com"
                    style={inputStyle}
                  />
                </Field>
                <Field label="Port">
                  <input
                    type="number"
                    value={newEmailForm.imap_port}
                    onChange={(e) => setNewEmailForm({ ...newEmailForm, imap_port: e.target.value })}
                    placeholder="993"
                    style={inputStyle}
                  />
                </Field>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <Field label="Username" hint="Defaults to the email address">
                  <input
                    type="text"
                    value={newEmailForm.imap_username}
                    onChange={(e) => setNewEmailForm({ ...newEmailForm, imap_username: e.target.value })}
                    placeholder={newEmailForm.email_address || 'username'}
                    style={inputStyle}
                  />
                </Field>
                <Field label="Password" required={!editingEmail}>
                  <input
                    type="password"
                    required={!editingEmail}
                    value={newEmailForm.imap_password}
                    onChange={(e) => setNewEmailForm({ ...newEmailForm, imap_password: e.target.value })}
                    placeholder={editingEmail ? 'Leave blank to keep existing' : 'App password'}
                    style={inputStyle}
                  />
                </Field>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', alignItems: 'end', marginBottom: '6px' }}>
                <Field label="Folder">
                  <input
                    type="text"
                    value={newEmailForm.imap_folder}
                    onChange={(e) => setNewEmailForm({ ...newEmailForm, imap_folder: e.target.value })}
                    placeholder="INBOX"
                    style={inputStyle}
                  />
                </Field>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', marginBottom: '10px' }}>
                  <input
                    type="checkbox"
                    checked={newEmailForm.imap_use_ssl}
                    onChange={(e) => setNewEmailForm({ ...newEmailForm, imap_use_ssl: e.target.checked })}
                  />
                  Use SSL/TLS
                </label>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '18px' }}>
                <button type="button" onClick={() => setShowAddEmail(false)} style={secondaryBtn}>Cancel</button>
                <button
                  type="submit"
                  disabled={addingEmail || !newEmailForm.email_address.trim()}
                  style={{ ...primaryBtn, opacity: (addingEmail || !newEmailForm.email_address.trim()) ? 0.6 : 1 }}
                >
                  {addingEmail ? 'Saving…' : editingEmail ? 'Save Changes' : 'Add'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAddMember && selected && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Add team member"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(17, 24, 39, 0.45)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}
          onClick={() => setShowAddMember(false)}
        >
          <div
            style={{
              ...cardStyle,
              width: '100%',
              maxWidth: '480px',
              maxHeight: '90vh',
              overflow: 'visible',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 40px rgba(0,0,0,0.18)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexShrink: 0 }}>
              <h3 style={{ fontSize: '15px', fontWeight: 700, margin: 0 }}>Add team member</h3>
              <button type="button" onClick={() => setShowAddMember(false)} style={iconBtn} aria-label="Close">
                <X size={20} />
              </button>
            </div>
            <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 14px', flexShrink: 0 }}>
              Search staff from the user directory. Manager and Lead roles are assigned here.
            </p>
            <Field label="User">
              <UserPicker
                value={memberForm.user?.user_id}
                onSelect={(u) => setMemberForm({ ...memberForm, user: u })}
                eligibleForTeam={selected.team_id}
                placeholder="Search by name or email…"
                autoFocus
              />
            </Field>
            <div style={{ marginTop: '12px', flexShrink: 0 }}>
              <Field label="Team role">
                <select
                  value={memberForm.org_role}
                  onChange={(e) => setMemberForm({ ...memberForm, org_role: e.target.value })}
                  style={inputStyle}
                >
                  <option value="manager">Manager</option>
                  <option value="lead">Lead</option>
                  <option value="member">Member</option>
                </select>
              </Field>
            </div>
            <div style={{ marginTop: '12px', flexShrink: 0 }}>
              <Field label="Department" hint="Optional — group this member under a product department.">
                <select
                  value={memberForm.department_id || ''}
                  onChange={(e) => setMemberForm({ ...memberForm, department_id: e.target.value })}
                  style={inputStyle}
                >
                  <option value="">Unassigned</option>
                  {productDepartments.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </Field>
            </div>
            <div style={{ marginTop: '12px', flexShrink: 0 }}>
                <button
                  type="button"
                  onClick={() => setMemberClientsOpen((open) => !open)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    background: '#F9FAFB',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    padding: '8px 10px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#374151',
                  }}
                  aria-expanded={memberClientsOpen}
                >
                  {memberClientsOpen ? 'Hide' : 'Assign'} clients (optional)
                  {' · '}
                  {memberForm.client_ids.length} selected
                  {productClients.length ? ` of ${productClients.length}` : ''}
                </button>
                {memberClientsOpen && (
                  <div style={{ marginTop: '8px' }}>
                    <ClientPickList
                      clients={productClients}
                      selectedIds={memberForm.client_ids}
                      onToggle={(clientId) => setMemberForm({
                        ...memberForm,
                        client_ids: memberForm.client_ids.includes(clientId)
                          ? memberForm.client_ids.filter((id) => id !== clientId)
                          : [...memberForm.client_ids, clientId],
                      })}
                    />
                  </div>
                )}
              </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '18px', flexShrink: 0 }}>
              <button type="button" onClick={() => setShowAddMember(false)} style={secondaryBtn}>Cancel</button>
              <button
                type="button"
                onClick={addMember}
                disabled={!memberForm.user}
                style={{ ...primaryBtn, opacity: memberForm.user ? 1 : 0.6 }}
              >
                Add to team
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddRaiser && selected && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(17, 24, 39, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '16px',
          }}
          onClick={() => setShowAddRaiser(false)}
        >
          <div
            style={{
              ...cardStyle,
              width: '100%',
              maxWidth: '440px',
              boxShadow: '0 20px 40px rgba(0,0,0,0.18)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ fontSize: '15px', fontWeight: 700, margin: 0 }}>Add ticket raiser</h3>
              <button type="button" onClick={() => setShowAddRaiser(false)} style={iconBtn} aria-label="Close">
                <X size={20} />
              </button>
        </div>
            <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 14px' }}>
              Choose an agent or client who can raise App Support tickets for this product.
              You can also assign the same access from the Users page.
            </p>
            <Field label="User">
              <UserPicker
                value={raiserForm.user?.user_id}
                onSelect={(u) => setRaiserForm({ user: u })}
                staffOnly={false}
                requestorsOnly
                eligibleForTeam={selected.team_id}
                placeholder="Search agents or clients…"
                autoFocus
              />
            </Field>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '18px' }}>
              <button type="button" onClick={() => setShowAddRaiser(false)} style={secondaryBtn}>Cancel</button>
              <button
                type="button"
                onClick={addRaiser}
                disabled={!raiserForm.user}
                style={{ ...primaryBtn, opacity: raiserForm.user ? 1 : 0.6 }}
              >
                Allow raise access
              </button>
      </div>
    </div>
        </div>
      )}

      {showCompaniesPanel && (
        <CompaniesPanel
          onClose={() => setShowCompaniesPanel(false)}
          onChanged={loadCompanies}
        />
      )}
    </div>
    </ModuleShell>
  );
}

function ClientPickList({ clients, selectedIds, onToggle, maxHeight = 160 }) {
  const [q, setQ] = useState('');
  const query = q.trim().toLowerCase();
  const filtered = !query
    ? clients
    : clients.filter((c) => (c.name || '').toLowerCase().includes(query));
  if (!clients.length) {
    return <p style={{ fontSize: '12px', color: '#6B7280', margin: 0 }}>No clients on this product yet.</p>;
  }
  return (
    <div>
      <input
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={`Search ${clients.length} clients…`}
        aria-label="Search clients"
        style={{
          width: '100%',
          padding: '6px 8px',
          borderRadius: '8px',
          border: '1px solid #D1D5DB',
          marginBottom: '6px',
          fontSize: '12px',
        }}
      />
      <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 6px' }}>
        {selectedIds.length} selected{query ? ` · ${filtered.length} match` : ''}
      </p>
      <div
        style={{
          maxHeight,
          overflowY: 'auto',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          padding: '6px 8px',
          background: '#fff',
        }}
      >
        {filtered.length ? filtered.map((c) => (
          <label
            key={c.client_id}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', minHeight: '28px' }}
          >
            <input
              type="checkbox"
              checked={selectedIds.includes(c.client_id)}
              onChange={() => onToggle(c.client_id)}
            />
            {c.name}
          </label>
        )) : (
          <p style={{ fontSize: '12px', color: '#9CA3AF', margin: 0 }}>No clients match “{q}”</p>
        )}
      </div>
    </div>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
        {label}{required ? ' *' : ''}
      </label>
      {children}
      {hint && <p style={{ fontSize: '12px', color: '#9CA3AF', margin: '6px 0 0' }}>{hint}</p>}
    </div>
  );
}

function Alert({ type, children }) {
  const ok = type === 'success';
  return (
    <div style={{
      padding: '6px 10px',
      borderRadius: '8px',
      marginBottom: '8px',
      background: ok ? '#D1FAE5' : '#FEE2E2',
      color: ok ? '#065F46' : '#B91C1C',
      fontSize: '12px',
    }}
    >
      {children}
    </div>
  );
}

function Badge({ children }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: '4px 10px',
      borderRadius: '999px',
      fontSize: '12px',
      fontWeight: '500',
      background: '#F3F4F6',
      color: '#4B5563',
    }}
    >
      {children}
    </span>
  );
}

const cardStyle = {
  background: 'white',
  border: '1px solid #E5E7EB',
  borderRadius: '12px',
  padding: '12px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
};

const inputStyle = {
  width: '100%',
  padding: '6px 8px',
  borderRadius: '8px',
  border: '1px solid #D1D5DB',
  fontSize: '12px',
  boxSizing: 'border-box',
};

const primaryBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 18px',
  background: '#002FA7',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontWeight: '600',
  fontSize: '12px',
  cursor: 'pointer',
};

const secondaryBtn = {
  padding: '10px 18px',
  background: 'white',
  color: '#374151',
  border: '1px solid #D1D5DB',
  borderRadius: '8px',
  fontWeight: '500',
  fontSize: '12px',
  cursor: 'pointer',
};

const dangerBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '4px 10px',
  background: 'white',
  color: '#B91C1C',
  border: '1px solid #FCA5A5',
  borderRadius: '8px',
  fontWeight: '600',
  fontSize: '12px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const iconBtn = { background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', padding: '4px' };
const th = { padding: '6px 8px', fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.03em' };
const td = { padding: '12px', fontSize: '12px', verticalAlign: 'top' };
const rolePill = { display: 'inline-block', padding: '2px 10px', borderRadius: '999px', background: '#EFF6FF', color: '#1D4ED8', fontSize: '12px', fontWeight: '600', textTransform: 'capitalize' };
const linkDanger = { color: '#B91C1C', border: 'none', background: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: '500' };
const linkBtn = { color: '#002FA7', border: 'none', background: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: '600' };
const linkMuted = { color: '#6B7280', border: 'none', background: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: '500' };
const linkAction = {
  color: '#002FA7',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  fontSize: '12px',
  fontWeight: '600',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
};

export default ProductsPage;
